import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import UiIcon from '../components/UiIcon';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';
import { buildCrmSwalBody, buildCrmSwalSteps, fireConfirmDialog, firePasswordConfirmDialog, wrapCrmSwalRich } from '../utils/swalConfirm';
import '../styles/admin-usuarios-premium.css';

function emptyForm() {
  return {
    id: null,
    user: '',
    correo: '',
    telefono: '',
    is_active: 1,
    chatwoot_agent_id: '',
    chatwoot_agent_name: '',
    medico_id: '',
    medico_nombre: '',
    administrativo_id: '',
    administrativo_nombre: '',
    callcenter_id: '',
    callcenter_nombre: '',
    password: '',
    roles: [],
    permissions: []
  };
}

function normalizeRoleKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function CallcenterCatalogField({
  value,
  callcenters,
  loading,
  loadError,
  takenByOther,
  disabled,
  fieldInputStyle,
  onChange
}) {
  const [query, setQuery] = useState('');
  const selected = callcenters.find((a) => String(a.id) === String(value));
  const filtered = useMemo(() => {
    const visibles = callcenters.filter(
      (a) => Number(a.is_visible ?? 0) === 1
    );

    const q = String(query || '').trim().toLowerCase();

    if (!q) return visibles;

    return visibles.filter((a) =>
      String(a.nombre || '').toLowerCase().includes(q)
    );
  }, [callcenters, query]);

  return (
    <section className="crm-au-section crm-au-section--medico-link">
      <div className="crm-au-section__head">
        <div className="crm-au-section__title">Persona de Call Center</div>
        <span className="crm-au-section__badge">Call Center</span>
      </div>
      <p className="crm-au-section__hint">
        Vinculá este usuario con una persona <strong>visible</strong> del catálogo de Call Center.
      </p>
      {loadError ? (
        <div className="crm-au-alert crm-au-alert--warn">{loadError}</div>
      ) : null}
      <label className="crm-au-field crm-au-field--stacked">
        <span className="crm-au-field__label">Buscar</span>
        <input
          className="crm-profile-input"
          style={fieldInputStyle}
          type="search"
          placeholder="Nombre…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled || loading}
        />
      </label>
      <label className="crm-au-field crm-au-field--stacked">
        <span className="crm-au-field__label">Persona Callcenter asociada</span>
        <select
          className="crm-profile-input"
          style={fieldInputStyle}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading || callcenters.length === 0}
        >
          <option value="">{loading ? 'Cargando…' : 'Seleccionar persona de Call Center…'}</option>
          {filtered.map((a) => {
            const takenUser = takenByOther.get(Number(a.id));
            return (
              <option key={a.id} value={a.id} disabled={Boolean(takenUser)}>
                {a.nombre}
                {takenUser ? ` (vinculado a ${takenUser})` : ''}
              </option>
            );
          })}
        </select>
      </label>
      {selected ? (
        <p className="crm-au-medico-link-selected">
          Operaciones dirigidas a <strong>{selected.nombre}</strong>
        </p>
      ) : (
        <p className="crm-au-medico-link-selected crm-au-medico-link-selected--muted">
          Elegí una persona del catálogo visible.
        </p>
      )}
    </section>
  );
}

function isMedicoRoleName(name) {
  return normalizeRoleKey(name) === 'medico';
}

function isAdministrativoRoleName(name) {
  return normalizeRoleKey(name) === 'administrativo';
}

function isCallcenterRoleName(name) {
  return normalizeRoleKey(name) === 'callcenter';
}

/** Orden jerárquico del directorio: admin → médico → administrativo → resto. */
const ROLE_SORT_RANK = { admin: 0, medico: 1, administrativo: 2 };
function roleSortRank(roleLabel) {
  const key = normalizeRoleKey(roleLabel);
  return ROLE_SORT_RANK[key] ?? 3;
}

/** Metadatos visuales por grupo de rol (encabezados premium del directorio). */
const ROLE_GROUP_META = {
  0: { key: 'admin', label: 'Administración', glyph: '🛡️', hint: 'Acceso total al sistema' },
  1: { key: 'medico', label: 'Médicos', glyph: '⚕️', hint: 'Evaluación y aprobación clínica' },
  2: { key: 'administrativo', label: 'Administrativos', glyph: '👤', hint: 'Gestión operativa y derivación' },
  3: { key: 'callcenter', label: 'Call Center', glyph: '🎧', hint: 'Contact Center y Conversaciones' },
  4: { key: 'otros', label: 'Otros roles', glyph: '👥', hint: 'Sin rol jerárquico asignado' }
};

const DIRECTORY_PAGE_SIZE_OPTIONS = [
  { value: '15', label: '15' },
  { value: '30', label: '30' },
  { value: '45', label: '45' },
  { value: '60', label: '60' },
  { value: '75', label: '75' },
  { value: 'all', label: 'Todos' }
];

function resolveChatwootAgentDisplayName(user, agentNameById) {
  const id = String(user?.chatwoot_agent_id || '').trim();
  if (!id) return '';
  const fromList = agentNameById?.get?.(id);
  const name = String(fromList || user?.chatwoot_agent_name || '').trim();
  return name || `Agente #${id}`;
}

function isMedicoRoleLabel(roleLabel) {
  return isMedicoRoleName(roleLabel);
}

function formatDoctorDisplayName(nombre) {
  const raw = String(nombre || '').trim();
  if (!raw) return '—';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  return `Dr/a. ${raw}`;
}

function MedicoCatalogField({
  value,
  medicos,
  loading,
  loadError,
  takenByOther,
  disabled,
  fieldInputStyle,
  onChange
}) {
  const [query, setQuery] = useState('');
  const selected = medicos.find((m) => String(m.id) === String(value));
  const filtered = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return medicos;
    return medicos.filter((m) => {
      const nombre = String(m.nombre || '').toLowerCase();
      const esp = String(m.especialidad || '').toLowerCase();
      const mat = String(m.matricula || '').toLowerCase();
      return nombre.includes(q) || esp.includes(q) || mat.includes(q);
    });
  }, [medicos, query]);

  return (
    <section className="crm-au-section crm-au-section--medico-link">
      <div className="crm-au-section__head">
        <div className="crm-au-section__title">Médico del catálogo</div>
        <span className="crm-au-section__badge">Derivaciones</span>
      </div>
      <p className="crm-au-section__hint">
        Vinculá este usuario con un médico <strong>visible</strong> en Gestión Derivación. Los pedidos derivados a
        ese médico llegarán a su bandeja de Pedidos/Recetas.
      </p>
      {loadError ? (
        <div className="crm-au-alert crm-au-alert--warn">{loadError}</div>
      ) : null}
      <label className="crm-au-field crm-au-field--stacked">
        <span className="crm-au-field__label">Buscar médico</span>
        <input
          className="crm-profile-input"
          style={fieldInputStyle}
          type="search"
          placeholder="Nombre, especialidad o matrícula…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled || loading}
        />
      </label>
      <label className="crm-au-field crm-au-field--stacked">
        <span className="crm-au-field__label">Médico asociado *</span>
        <select
          className="crm-profile-input"
          style={fieldInputStyle}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading || medicos.length === 0}
        >
          <option value="">{loading ? 'Cargando médicos…' : 'Seleccionar médico visible…'}</option>
          {filtered.map((m) => {
            const takenUser = takenByOther.get(Number(m.id));
            const label = formatDoctorDisplayName(m.nombre);
            const meta = [m.especialidad, m.matricula].filter(Boolean).join(' · ');
            return (
              <option key={m.id} value={m.id} disabled={Boolean(takenUser)}>
                {label}
                {meta ? ` — ${meta}` : ''}
                {takenUser ? ` (vinculado a ${takenUser})` : ''}
              </option>
            );
          })}
        </select>
      </label>
      {selected ? (
        <p className="crm-au-medico-link-selected">
          Derivaciones dirigidas a <strong>{formatDoctorDisplayName(selected.nombre)}</strong>
        </p>
      ) : (
        <p className="crm-au-medico-link-selected crm-au-medico-link-selected--muted">
          Elegí un médico del catálogo visible.
        </p>
      )}
    </section>
  );
}

function AdministrativoCatalogField({
  value,
  administrativos,
  loading,
  loadError,
  takenByOther,
  disabled,
  fieldInputStyle,
  onChange
}) {
  const [query, setQuery] = useState('');
  const selected = administrativos.find((a) => String(a.id) === String(value));
  const filtered = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return administrativos;
    return administrativos.filter((a) => String(a.nombre || '').toLowerCase().includes(q));
  }, [administrativos, query]);

  return (
    <section className="crm-au-section crm-au-section--medico-link">
      <div className="crm-au-section__head">
        <div className="crm-au-section__title">Persona administrativa</div>
        <span className="crm-au-section__badge">Recetas a emitir</span>
      </div>
      <p className="crm-au-section__hint">
        Vinculá este usuario con una persona <strong>visible</strong> del catálogo administrativo. Al aprobar
        tratamientos con emisor Administrativo, esa persona quedará asignada al pedido.
      </p>
      {loadError ? (
        <div className="crm-au-alert crm-au-alert--warn">{loadError}</div>
      ) : null}
      <label className="crm-au-field crm-au-field--stacked">
        <span className="crm-au-field__label">Buscar</span>
        <input
          className="crm-profile-input"
          style={fieldInputStyle}
          type="search"
          placeholder="Nombre…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled || loading}
        />
      </label>
      <label className="crm-au-field crm-au-field--stacked">
        <span className="crm-au-field__label">Administrativo asociado *</span>
        <select
          className="crm-profile-input"
          style={fieldInputStyle}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading || administrativos.length === 0}
        >
          <option value="">{loading ? 'Cargando…' : 'Seleccionar administrativo visible…'}</option>
          {filtered.map((a) => {
            const takenUser = takenByOther.get(Number(a.id));
            return (
              <option key={a.id} value={a.id} disabled={Boolean(takenUser)}>
                {a.nombre}
                {takenUser ? ` (vinculado a ${takenUser})` : ''}
              </option>
            );
          })}
        </select>
      </label>
      {selected ? (
        <p className="crm-au-medico-link-selected">
          Emisión asignada a <strong>{selected.nombre}</strong>
        </p>
      ) : (
        <p className="crm-au-medico-link-selected crm-au-medico-link-selected--muted">
          Elegí una persona del catálogo visible.
        </p>
      )}
    </section>
  );
}

function CrmToggleSwitch({ checked, disabled, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      className={`crm-au-switch${checked ? ' is-on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="crm-au-switch__thumb" aria-hidden="true" />
    </button>
  );
}

function CrmPasswordField({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  required = false,
  layout = 'inline'
}) {
  const [visible, setVisible] = useState(false);
  return (
    <label
      className={`crm-au-field crm-au-field--password${layout === 'stacked' ? ' crm-au-field--stacked' : ''
        }`}
      htmlFor={id}
    >
      <span className="crm-au-field__label">
        {label}
        {required ? ' *' : ''}
      </span>
      <div className="crm-au-password-wrap">
        <input
          id={id}
          className="crm-profile-input crm-au-password-wrap__input"
          type={visible ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
        <button
          type="button"
          className="crm-au-password-wrap__toggle"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          tabIndex={-1}
        >
          <UiIcon name={visible ? 'eye-off' : 'eye'} />
        </button>
      </div>
    </label>
  );
}

const PERMISSION_LABELS = {
  ver_contactos: 'Contactos',
  ver_trazabilidad_asignaciones: 'Trazabilidad asignaciones (Chatwoot)',
  ver_conversaciones: 'Conversaciones',
  eliminar_conversaciones: 'Eliminar conversaciones (Chatwoot)',
  asignar_agente_conversacion: 'Asignar operador a conversaciones (Chatwoot)',
  ver_bot_farmacia: 'Bot Farmacia',
  ver_encuesta_bot: 'Encuesta bot (generador)',
  ver_encuesta_bot_review: 'Review encuestas bot',
  ver_gestion_derivacion: 'Gestión Derivación',
  admin_medicos_visibilidad: 'Médicos en derivación (visibilidad)',
  admin_administrativos_visibilidad: 'Administrativos en emisión (visibilidad)',
  admin_sucursales_farmacia: 'Sucursales de Farmacia (CRUD)',
  admin_poligonos_farmacia: 'Polígonos de Farmacia (CRUD)',
  admin_horarios_botrecetasatencion: 'Horarios de BotRecetas (CRUD)',
  ver_manual_administrativo: 'Manual administrativo (Derivación y Recetas)',
  ver_manual_medico: 'Manual médico (Pedidos/Recetas)',
  crear_pedido_alta_rapida: 'Alta rápida de pedido',
  ver_pedido_recetas_medico: 'Pedidos/Recetas',
  ver_pedidos: 'Última milla',
  ver_pedidos_dispensa: 'Contact Center',
  ver_pulse_ledger: 'Trazabilidad',
  ver_trazabilidad_recetas: 'Trazabilidad recetas (ciclo completo)',
  ver_recetas_cruzadas: 'Recetas cruzadas',
  ver_recetas_emitir: 'Recetas a emitir',
  manual_conversaciones: 'Manual de conversaciones',
  ver_directorio: 'Directorio de médicos y administrativos',
};

function formatPermissionLabel(code) {
  const key = String(code || '').trim();
  if (!key) return '—';
  if (PERMISSION_LABELS[key]) return PERMISSION_LABELS[key];
  return key
    .replace(/^(ver_|admin_|eliminar_|asignar_)/i, '')
    .split('_')
    .filter(Boolean)
    .map((part) => {
      const lower = part.toLowerCase();
      if (lower === 'bot') return 'Bot';
      if (lower === 'medico' || lower === 'medicos') return 'Médico';
      if (lower === 'recetas') return 'Recetas';
      if (lower === 'crm') return 'CRM';
      if (lower === 'chatwoot') return 'Chatwoot';
      if (lower === 'dispensa') return 'Dispensa';
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
}
const RESERVED_N8N_USER = {
  user: 'admin',
  correo: 'admin@gmail.com'
};

/**
 * Clave fija requerida para confirmar la eliminación de un usuario en el directorio.
 * Validada en el frontend (no toca backend). CAMBIAR este valor por la clave real.
 */
const DELETE_USER_CONFIRM_PASSWORD = 'Farma951';

/** Indica si el usuario tiene rol admin (no se puede eliminar). */
function isAdminRoleLabel(roleLabel) {
  return normalizeRoleKey(roleLabel) === 'admin';
}

/** Agentes Chatwoot que no deben asignarse a ningún usuario CRM (cuenta admin / rotación de tokens). */
const BLOCKED_CHATWOOT_AGENT_EMAILS = new Set(['farmagsa@mail.com', 'farmagsa@gmail.com']);
const BLOCKED_CHATWOOT_AGENT_ID = '3';

function isReservedForN8nUser(userData) {
  const normalizedUser = String(userData?.user || '').trim().toLowerCase();
  const normalizedCorreo = String(userData?.correo || '').trim().toLowerCase();
  return normalizedUser === RESERVED_N8N_USER.user && normalizedCorreo === RESERVED_N8N_USER.correo;
}

function shortenDirectoryLabel(value, max = 22) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.length <= max) return raw;
  return `${raw.slice(0, max - 1)}…`;
}

function findUserDuplicateConflict(users, { user, correo, excludeUserId } = {}) {
  const userKey = String(user || '').trim().toLowerCase();
  const correoKey = String(correo || '').trim().toLowerCase();
  let userMatch = null;
  let correoMatch = null;
  for (const row of users || []) {
    if (excludeUserId != null && Number(row.id) === Number(excludeUserId)) continue;
    const rowUser = String(row.user || row.username || '').trim().toLowerCase();
    const rowCorreo = String(row.correo || row.email || '').trim().toLowerCase();
    if (userKey && rowUser === userKey) userMatch = row;
    if (correoKey && rowCorreo === correoKey) correoMatch = row;
  }
  return { userMatch, correoMatch };
}

function isBlockedChatwootAgentEmail(email) {
  return BLOCKED_CHATWOOT_AGENT_EMAILS.has(String(email || '').trim().toLowerCase());
}

function isBlockedChatwootAgent(agent) {
  if (!agent) return false;
  const email = String(agent.email || '').trim().toLowerCase();
  if (isBlockedChatwootAgentEmail(email)) return true;
  const agentId = String(agent.id ?? '').trim();
  const name = String(agent.name || '').trim().toLowerCase();
  if (agentId === BLOCKED_CHATWOOT_AGENT_ID && (email.includes('farmagsa') || name.includes('call center'))) {
    return true;
  }
  return false;
}

function blockedChatwootAgentLabel(agent) {
  const email = String(agent?.email || '').trim().toLowerCase();
  if (email) return email;
  const name = String(agent?.name || '').trim();
  if (name) return name;
  return agent?.id != null ? `agente #${agent.id}` : 'agente bloqueado';
}

function getAgentInitials(name, email) {
  const n = String(name || '').trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  const e = String(email || '').trim();
  return e ? e.slice(0, 2).toUpperCase() : 'AG';
}

function filterSelectableChatwootAgents(agents, users, editingUserId, ownAgentId = '') {
  const ownId = String(ownAgentId || '').trim();
  const editingId = editingUserId != null ? Number(editingUserId) : null;

  return (agents || []).filter((agent) => {
    if (isBlockedChatwootAgent(agent)) return false;

    const agentId = String(agent.id);
    if (ownId && agentId === ownId) return true;

    const takenByOther = (users || []).some(
      (u) =>
        (editingId == null || Number(u.id) !== editingId) &&
        String(u.chatwoot_agent_id || '') === agentId
    );
    return !takenByOther;
  });
}

function buildChatwootFieldsFromUser(u, agents = []) {
  const agentId =
    u?.chatwoot_agent_id != null && u?.chatwoot_agent_id !== ''
      ? String(u.chatwoot_agent_id)
      : '';
  const picked = agents.find((a) => String(a.id) === agentId);
  return {
    chatwoot_agent_id: agentId,
    chatwoot_agent_name: picked?.name || ''
  };
}

function ChatwootAgentField({
  value,
  agentName,
  savingLink,
  onAgentIdChange,
  onAgentNameChange,
  onSaveAgentName,
  agents,
  pickerAgents,
  loading,
  loadError,
  disabled,
  displayName,
  emailLooksValid,
  onCreateInChatwoot,
  creatingChatwoot,
  onDeleteFromChatwoot,
  deletingChatwoot,
  savingAgentName,
  fieldInputStyle,
  createEmailBlocked = false
}) {
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const linked = Boolean(value);
  const selected = (agents || []).find((a) => String(a.id) === String(value));
  const savedName = String(selected?.name || '').trim();
  const draftName = String(agentName ?? '');
  const displayAgentName = draftName.trim() || savedName || 'Sin nombre';
  const nameDirty = linked && draftName.trim() !== savedName;
  const availableAgents = pickerAgents ?? agents ?? [];
  const linkedBlocked = linked && isBlockedChatwootAgent(selected);

  useEffect(() => {
    setShowAgentPicker(false);
  }, [value]);

  const badgeClass = savingLink
    ? 'crm-cw-agent-panel__badge is-pending'
    : linked
      ? 'crm-cw-agent-panel__badge is-linked'
      : 'crm-cw-agent-panel__badge is-empty';

  const badgeLabel = savingLink ? 'Guardando…' : linked ? 'Vinculado y activo' : 'Sin vínculo';

  return (
    <section className="crm-cw-agent-panel crm-cw-agent-panel--in-admin" aria-label="Integración Chatwoot WhatsApp">
      <header className="crm-cw-agent-panel__head">
        <div className="crm-cw-agent-panel__brand">
          <div className="crm-cw-agent-panel__icon" aria-hidden="true">
            💬
          </div>
          <div className="crm-cw-agent-panel__titles">
            <div className="crm-cw-agent-panel__title">WhatsApp · Chatwoot</div>
            <div className="crm-cw-agent-panel__subtitle">
              Operador para conversaciones del CRM ({displayName || 'usuario'})
            </div>
          </div>
        </div>
        <span className={badgeClass}>{badgeLabel}</span>
      </header>

      <div className="crm-cw-agent-panel__body">
        {savingLink ? (
          <div className="crm-cw-agent-panel__alert is-pending">Guardando vínculo en la base de datos…</div>
        ) : null}
        {loading ? (
          <div className="crm-cw-agent-panel__alert is-pending">Cargando agentes desde Chatwoot…</div>
        ) : null}
        {!loading && loadError ? (
          <div className="crm-cw-agent-panel__alert is-error">{loadError}</div>
        ) : null}

        {linkedBlocked && !savingLink ? (
          <div className="crm-cw-agent-panel__alert is-error">
            El operador <strong>{blockedChatwootAgentLabel(selected)}</strong> no puede usarse en el CRM (cuenta
            administrativa de Chatwoot). Quitá el vínculo y elegí <strong>Liliana</strong>, <strong>Verónica</strong> u
            otro agente operativo.
          </div>
        ) : null}

        {linked && !savingLink && !linkedBlocked ? (
          <div className="crm-cw-agent-panel__alert is-ok">
            <span aria-hidden="true">✓</span>
            <span>
              Vínculo activo con <strong>{displayAgentName}</strong> (agente #{value}).
            </span>
          </div>
        ) : null}

        {!linked && !savingLink ? (
          <div className="crm-cw-agent-panel__alert is-pending">
            Sin operador de Chatwoot asignado para este usuario CRM.
          </div>
        ) : null}

        {!linked ? (
          <>
            <p style={{ margin: 0, fontSize: 12, color: '#5f7895', lineHeight: 1.5 }}>
              Solo se muestran agentes <strong>libres</strong> (no asignados a otro usuario CRM). Si no hay ninguno,
              creá uno nuevo.
            </p>

            {availableAgents.length > 0 ? (
              <div className="crm-cw-agent-picker" role="listbox" aria-label="Agentes disponibles">
                {availableAgents.map((agent) => {
                  const isSelected = String(agent.id) === String(value);
                  return (
                    <button
                      key={agent.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`crm-cw-agent-card${isSelected ? ' is-selected' : ''}`}
                      onClick={() => onAgentIdChange(String(agent.id))}
                      disabled={disabled || loading || savingLink}
                    >
                      <div className="crm-cw-agent-card__top">
                        <div className="crm-cw-agent-card__avatar">
                          {getAgentInitials(agent.name, agent.email)}
                        </div>
                        <div className="crm-cw-agent-card__name">{agent.name || `Agente #${agent.id}`}</div>
                      </div>
                      <div className="crm-cw-agent-card__meta">
                        {agent.email || 'sin correo'} · #{agent.id}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              !loading && !loadError ? (
                <div className="crm-cw-agent-panel__alert is-pending">
                  {(agents || []).length > 0
                    ? 'Todos los agentes de Chatwoot ya están asignados a otros usuarios CRM. Creá uno nuevo abajo.'
                    : 'No hay agentes en Chatwoot. Creá el primero con el botón de abajo.'}
                </div>
              ) : null
            )}

            {createEmailBlocked ? (
              <div className="crm-cw-agent-panel__alert is-error">
                El correo del usuario CRM no puede ser <strong>farmagsa@mail.com</strong> ni{' '}
                <strong>farmagsa@gmail.com</strong> para crear agente Chatwoot. Usá el mail del operador (ej.{' '}
                administrativo_1@mail.com).
              </div>
            ) : null}

            <div className="crm-cw-agent-name-box">
              <div className="crm-cw-agent-name-box__label">Nombre en Chatwoot (obligatorio al crear)</div>
              <input
                className="crm-profile-input crm-cw-agent-text-input"
                style={fieldInputStyle}
                type="text"
                placeholder={`Ej: operador distinto a «${displayName || 'usuario'}»`}
                value={agentName}
                onChange={(e) => onAgentNameChange(e.target.value)}
                disabled={disabled || loading || creatingChatwoot || savingLink || createEmailBlocked}
              />
              <div className="crm-cw-agent-name-box__hint">
                Este nombre se usa al crear el agente en Chatwoot y en los chats de WhatsApp. No usa el usuario
                CRM automáticamente.
              </div>
            </div>

            <div className="crm-cw-agent-panel__actions">
              <button
                type="button"
                className="crm-cw-agent-btn crm-cw-agent-btn--primary"
                onClick={onCreateInChatwoot}
                disabled={
                  disabled ||
                  loading ||
                  creatingChatwoot ||
                  savingLink ||
                  createEmailBlocked ||
                  !emailLooksValid ||
                  !String(displayName || '').trim() ||
                  !draftName.trim()
                }
              >
                {creatingChatwoot ? 'Creando agente…' : '+ Crear agente en Chatwoot'}
              </button>
            </div>
          </>
        ) : linkedBlocked ? (
          <div className="crm-cw-agent-panel__actions">
            <button
              type="button"
              className="crm-cw-agent-btn crm-cw-agent-btn--danger"
              onClick={() => onAgentIdChange('')}
              disabled={disabled || loading || savingLink}
            >
              Quitar vínculo con agente bloqueado
            </button>
          </div>
        ) : (
          <>
            <div className="crm-cw-agent-linked">
              <div className="crm-cw-agent-linked__hero">
                <div className="crm-cw-agent-linked__avatar" aria-hidden="true">
                  {getAgentInitials(displayAgentName, selected?.email)}
                </div>
                <div className="crm-cw-agent-linked__info">
                  <div className="crm-cw-agent-linked__name">{displayAgentName}</div>
                  <div className="crm-cw-agent-linked__meta">{selected?.email || 'Sin correo en Chatwoot'}</div>
                  <div className="crm-cw-agent-linked__chips">
                    <span className="crm-cw-agent-chip">Agente #{value}</span>
                    <span className="crm-cw-agent-chip">CRM: {displayName || '—'}</span>
                    {selected?.confirmed === false ? (
                      <span className="crm-cw-agent-chip">Pendiente de activación</span>
                    ) : (
                      <span className="crm-cw-agent-chip crm-cw-agent-chip--ok">Activo en WhatsApp</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="crm-cw-agent-name-box">
              <div className="crm-cw-agent-name-box__label">Nombre visible en chats de WhatsApp</div>
              <div className="crm-cw-agent-name-box__row">
                <input
                  className="crm-profile-input crm-cw-agent-text-input"
                  type="text"
                  placeholder="Ej: Liliana · Administración"
                  value={agentName}
                  onChange={(e) => onAgentNameChange(e.target.value)}
                  disabled={disabled || loading || creatingChatwoot || savingAgentName}
                />
                <button
                  type="button"
                  className="crm-cw-agent-btn crm-cw-agent-btn--primary crm-cw-agent-btn--icon"
                  onClick={onSaveAgentName}
                  disabled={
                    disabled ||
                    loading ||
                    creatingChatwoot ||
                    savingAgentName ||
                    !draftName.trim() ||
                    !nameDirty
                  }
                  title={savingAgentName ? 'Guardando…' : 'Aplicar nombre en Chatwoot'}
                  aria-label="Aplicar nombre en Chatwoot"
                >
                  <UiIcon name="check" size={15} />
                </button>
                {!showAgentPicker ? (
                  <>
                    <button
                      type="button"
                      className="crm-cw-agent-btn crm-cw-agent-btn--ghost crm-cw-agent-btn--icon"
                      onClick={() => setShowAgentPicker(true)}
                      disabled={disabled || loading || savingLink || deletingChatwoot}
                      title="Cambiar o quitar vínculo con el agente"
                      aria-label="Cambiar o quitar vínculo"
                    >
                      <UiIcon name="refresh" size={15} />
                    </button>
                    <button
                      type="button"
                      className="crm-cw-agent-btn crm-cw-agent-btn--danger crm-cw-agent-btn--icon"
                      onClick={onDeleteFromChatwoot}
                      disabled={
                        disabled ||
                        loading ||
                        creatingChatwoot ||
                        deletingChatwoot ||
                        savingLink ||
                        savingAgentName
                      }
                      title="Eliminar el agente en Chatwoot"
                      aria-label="Eliminar en Chatwoot"
                    >
                      <UiIcon name="trash" size={15} />
                    </button>
                  </>
                ) : null}
              </div>
              {/* <div className="crm-cw-agent-name-box__hint">
                Cambia cómo aparece el operador frente al cliente. El usuario CRM ({displayName || '—'}) no se
                modifica.
              </div> */}
            </div>

            {showAgentPicker ? (
              <div className="crm-cw-agent-change">
                <div className="crm-cw-agent-name-box__label">Elegir otro agente o quitar vínculo</div>
                <div className="crm-cw-agent-picker">
                  <button
                    type="button"
                    className={`crm-cw-agent-card${!value ? ' is-selected' : ''}`}
                    onClick={() => onAgentIdChange('')}
                    disabled={disabled || loading || savingLink}
                  >
                    <div className="crm-cw-agent-card__name">Sin vínculo</div>
                    <div className="crm-cw-agent-card__meta">Desasociar este usuario CRM</div>
                  </button>
                  {availableAgents.map((agent) => (
                    <button
                      key={agent.id}
                      type="button"
                      className={`crm-cw-agent-card${String(agent.id) === String(value) ? ' is-selected' : ''}`}
                      onClick={() => onAgentIdChange(String(agent.id))}
                      disabled={disabled || loading || savingLink}
                    >
                      <div className="crm-cw-agent-card__top">
                        <div className="crm-cw-agent-card__avatar">
                          {getAgentInitials(agent.name, agent.email)}
                        </div>
                        <div className="crm-cw-agent-card__name">{agent.name || `Agente #${agent.id}`}</div>
                      </div>
                      <div className="crm-cw-agent-card__meta">
                        {agent.email || 'sin correo'} · #{agent.id}
                      </div>
                    </button>
                  ))}
                </div>
                {availableAgents.length === 0 ? (
                  <div className="crm-cw-agent-panel__alert is-pending">
                    No hay otros agentes libres. Podés quitar el vínculo o crear uno nuevo en Chatwoot.
                  </div>
                ) : null}
                <button
                  type="button"
                  className="crm-cw-agent-btn crm-cw-agent-btn--ghost"
                  onClick={() => setShowAgentPicker(false)}
                  disabled={savingLink}
                >
                  Listo
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function AdminUserPanelBackButton({ onClick }) {
  return (
    <button type="button" className="crm-au-edit-back" onClick={onClick}>
      <span className="crm-au-edit-back__icon" aria-hidden="true">
        ←
      </span>
      Volver
    </button>
  );
}

function N8nReservedUserPanel({
  user,
  correo,
  telefono,
  isActive,
  roleLabel,
  medicoNombre,
  chatwootSummary,
  permissionItems,
  onBack
}) {
  const permCount = permissionItems.length;
  return (
    <section className="crm-admin-users-form crm-au-edit-form crm-au-n8n-view" aria-label="Usuario n8n solo lectura">
      <div className="crm-au-n8n-view__head">
        <div className="crm-au-detail-head crm-au-detail-head--toolbar">
          <AdminUserPanelBackButton onClick={onBack} />
          <div className="crm-au-detail-head__content">
            <div className="crm-au-detail-head__title">{user || 'Usuario'}</div>
            <div className="crm-au-detail-head__meta">
              {roleLabel ? `Rol · ${roleLabel}` : 'Sin rol asignado'}
              {correo ? ` · ${correo}` : ''}
              {medicoNombre ? ` · ${formatDoctorDisplayName(medicoNombre)}` : ''}
              {chatwootSummary ? (
                <>
                  {' · Agente Chatwoot · '}
                  <strong
                    className={
                      chatwootSummary.linked
                        ? 'crm-au-detail-head__meta-accent'
                        : 'crm-au-detail-head__meta-muted'
                    }
                  >
                    {chatwootSummary.metaLabel}
                  </strong>
                </>
              ) : null}
            </div>
          </div>
          <div className="crm-au-detail-head__pills">
            <span className="crm-au-status-pill crm-au-status-pill--n8n">n8n</span>
            <span
              className={`crm-au-status-pill ${isActive ? 'crm-au-status-pill--active' : 'crm-au-status-pill--inactive'
                }`}
            >
              {isActive ? 'Activo' : 'Inactivo'}
            </span>
          </div>
        </div>
        <div className="crm-au-n8n-banner" role="status">
          <span className="crm-au-n8n-banner__icon" aria-hidden="true">
            <UiIcon name="lock" size={18} />
          </span>
          <div className="crm-au-n8n-banner__copy">
            <div className="crm-au-n8n-banner__title">Cuenta reservada para automatización n8n</div>
            <p className="crm-au-n8n-banner__text">
              Podés consultar datos, rol y permisos. La edición desde Admin Usuarios está deshabilitada para
              proteger la integración con n8n.
            </p>
          </div>
        </div>
      </div>

      <div className="crm-au-n8n-view__body">
        <div className="crm-au-n8n-grid">
          <article className="crm-au-n8n-card">
            <header className="crm-au-n8n-card__head">
              <span className="crm-au-n8n-card__icon" aria-hidden="true">
                <UiIcon name="user" size={16} />
              </span>
              <h4 className="crm-au-n8n-card__title">Datos del usuario</h4>
            </header>
            <dl className="crm-au-n8n-kv">
              <div className="crm-au-n8n-kv__row">
                <dt>Usuario</dt>
                <dd>{user || '—'}</dd>
              </div>
              <div className="crm-au-n8n-kv__row">
                <dt>Correo</dt>
                <dd>{correo || '—'}</dd>
              </div>
              <div className="crm-au-n8n-kv__row">
                <dt>Teléfono</dt>
                <dd>{telefono || '—'}</dd>
              </div>
              <div className="crm-au-n8n-kv__row">
                <dt>Estado</dt>
                <dd>{isActive ? 'Activo' : 'Inactivo'}</dd>
              </div>
            </dl>
          </article>

          <article className="crm-au-n8n-card">
            <header className="crm-au-n8n-card__head">
              <span className="crm-au-n8n-card__icon" aria-hidden="true">
                <UiIcon name="shield" size={16} />
              </span>
              <h4 className="crm-au-n8n-card__title">Rol y permisos</h4>
              <span className="crm-au-n8n-card__badge">{permCount} activos</span>
            </header>
            <p className="crm-au-n8n-card__lead">
              Rol asignado: <strong>{roleLabel || 'Sin rol'}</strong>
            </p>
            <div className="crm-au-n8n-perms">
              {permissionItems.length ? (
                permissionItems.map((perm) => (
                  <span key={perm.id} className="crm-au-n8n-perms__chip" title={perm.code}>
                    {perm.label}
                  </span>
                ))
              ) : (
                <span className="crm-au-n8n-perms__empty">Sin permisos cargados</span>
              )}
            </div>
          </article>

          <article className="crm-au-n8n-card crm-au-n8n-card--whatsapp">
            <header className="crm-au-n8n-card__head">
              <span className="crm-au-n8n-card__icon crm-au-n8n-card__icon--wa" aria-hidden="true">
                💬
              </span>
              <h4 className="crm-au-n8n-card__title">WhatsApp · Chatwoot</h4>
            </header>
            {chatwootSummary?.linked ? (
              <p className="crm-au-n8n-card__lead">
                Operador vinculado: <strong>{chatwootSummary.metaLabel}</strong>
              </p>
            ) : (
              <p className="crm-au-n8n-card__lead crm-au-n8n-card__lead--muted">
                Sin operador de Chatwoot asignado a esta cuenta CRM.
              </p>
            )}
            <p className="crm-au-n8n-card__hint">
              El vínculo con WhatsApp no se gestiona desde este panel para usuarios n8n.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

function RolesPermissionsAccordion({
  expandedPanel,
  onTogglePanel,
  roleOptions,
  form,
  permissions,
  syncingRolePerms,
  onSelectRole,
  disabled,
  radioName,
  rolePickerVariant = 'radio'
}) {
  const selectedRoleLabel = roleOptions.find((r) => form.roles.includes(Number(r.id)))?.name || '';
  const permCount = form.permissions.length;
  const useRoleSwitches = rolePickerVariant === 'switch';

  const rolesBlock = useRoleSwitches ? (
    <div className="crm-au-role-switches">
      {roleOptions.map((r) => {
        const isOn = form.roles.includes(Number(r.id));
        return (
          <div key={r.id} className={`crm-au-role-switch-row${isOn ? ' is-active' : ''}`}>
            <span className="crm-au-role-switch-row__name">{r.name}</span>
            <CrmToggleSwitch
              checked={isOn}
              disabled={disabled || syncingRolePerms}
              ariaLabel={`Rol ${r.name}`}
              onChange={(next) => {
                if (next) onSelectRole(Number(r.id));
              }}
            />
          </div>
        );
      })}
    </div>
  ) : (
    <div className="crm-admin-rp-accordion__content">
      {roleOptions.map((r) => (
        <label key={r.id} className="crm-admin-rp-accordion__item">
          <input
            type="radio"
            name={radioName}
            checked={form.roles.includes(Number(r.id))}
            onChange={() => onSelectRole(Number(r.id))}
            disabled={disabled}
          />
          <span>{r.name}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="crm-admin-rp-accordions">
      {useRoleSwitches ? (
        <section className="crm-au-section crm-au-section--roles">
          <div className="crm-au-section__head">
            <div className="crm-au-section__title">Rol del usuario</div>
            <span className="crm-au-section__badge">Uno solo</span>
          </div>
          <p className="crm-au-section__hint">
            Activá el rol que corresponda. Los permisos se cargan automáticamente.
          </p>
          {rolesBlock}
          {selectedRoleLabel ? (
            <p className="crm-au-role-selected">
              Seleccionado: <strong>{selectedRoleLabel}</strong>
              {syncingRolePerms ? ' · sincronizando permisos…' : ''}
            </p>
          ) : (
            <p className="crm-au-role-selected crm-au-role-selected--muted">Elegí un rol para continuar.</p>
          )}
        </section>
      ) : (
        <div className={`crm-admin-rp-accordion${expandedPanel === 'roles' ? ' is-open' : ''}`}>
          <button
            type="button"
            className="crm-admin-rp-accordion__trigger"
            onClick={() => onTogglePanel('roles')}
            aria-expanded={expandedPanel === 'roles'}
          >
            <span className="crm-admin-rp-accordion__title">Roles (solo uno)</span>
            {selectedRoleLabel && expandedPanel !== 'roles' ? (
              <span className="crm-admin-rp-accordion__hint">{selectedRoleLabel}</span>
            ) : null}
            <span className="crm-admin-rp-accordion__chevron" aria-hidden="true">
              {expandedPanel === 'roles' ? '▾' : '▸'}
            </span>
          </button>
          <div className={`crm-admin-rp-accordion__body${expandedPanel === 'roles' ? ' is-open' : ''}`}>
            <div className="crm-admin-rp-accordion__body-inner">{rolesBlock}</div>
          </div>
        </div>
      )}

      <div className={`crm-admin-rp-accordion${expandedPanel === 'permissions' ? ' is-open' : ''}`}>
        <button
          type="button"
          className="crm-admin-rp-accordion__trigger"
          onClick={() => onTogglePanel('permissions')}
          aria-expanded={expandedPanel === 'permissions'}
        >
          <span className="crm-admin-rp-accordion__title">
            Permisos {syncingRolePerms ? '(sincronizando...)' : ''}
          </span>
          {permCount > 0 && expandedPanel !== 'permissions' ? (
            <span className="crm-admin-rp-accordion__hint">{permCount} activos</span>
          ) : null}
          <span className="crm-admin-rp-accordion__chevron" aria-hidden="true">
            {expandedPanel === 'permissions' ? '▾' : '▸'}
          </span>
        </button>
        <div className={`crm-admin-rp-accordion__body${expandedPanel === 'permissions' ? ' is-open' : ''}`}>
          <div className="crm-admin-rp-accordion__body-inner">
            <div className="crm-admin-rp-accordion__content crm-admin-rp-accordion__content--scroll">
              {permissions.map((p) => (
                <label key={p.id} className="crm-admin-rp-accordion__item">
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(Number(p.id))}
                    disabled
                    readOnly
                  />
                  <span>{formatPermissionLabel(p.code)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getRoleLabelFromUser(userData) {
  const rawRole =
    userData?.role_name ||
    userData?.role ||
    userData?.rol ||
    userData?.user_type ||
    userData?.tipo_usuario ||
    (Array.isArray(userData?.roles) ? userData.roles[0]?.name || userData.roles[0] : '');
  const cleaned = String(rawRole || '').trim();
  return cleaned || 'Sin rol';
}

function AdminUsuarios() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createModalStep, setCreateModalStep] = useState(1);
  const [editFormStep, setEditFormStep] = useState(1);
  const createModalDialogRef = useRef(null);
  const isCreateModalOpenRef = useRef(false);
  const [expandedRpPanel, setExpandedRpPanel] = useState(null);
  const [syncingRolePerms, setSyncingRolePerms] = useState(false);
  const [userRoleMap, setUserRoleMap] = useState({});
  const [query, setQuery] = useState('');
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryPageSize, setDirectoryPageSize] = useState('15');
  const [directoryPageSizeOpen, setDirectoryPageSizeOpen] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState('');
  const [previewLabel, setPreviewLabel] = useState('');
  const [chatwootAgents, setChatwootAgents] = useState([]);
  const [chatwootAgentsLoading, setChatwootAgentsLoading] = useState(false);
  const [chatwootAgentsError, setChatwootAgentsError] = useState('');
  const [visibleMedicos, setVisibleMedicos] = useState([]);
  const [visibleMedicosLoading, setVisibleMedicosLoading] = useState(false);
  const [visibleMedicosError, setVisibleMedicosError] = useState('');
  const [visibleAdministrativos, setVisibleAdministrativos] = useState([]);
  const [visibleAdministrativosLoading, setVisibleAdministrativosLoading] = useState(false);
  const [visibleAdministrativosError, setVisibleAdministrativosError] = useState('');
  const [visibleCallcenters, setVisibleCallcenters] = useState([]);
  const [visibleCallcentersLoading, setVisibleCallcentersLoading] = useState(false);
  const [visibleCallcentersError, setVisibleCallcentersError] = useState('');
  const [chatwootCreateConfig, setChatwootCreateConfig] = useState({
    create_without_invite: false,
    platform_configured: false,
    password_valid: false,
    password_issues: [],
    password_hint: ''
  });
  const chatwootCreateWithoutInvite = Boolean(chatwootCreateConfig.create_without_invite);
  const [creatingChatwootAgent, setCreatingChatwootAgent] = useState(false);
  const [deletingChatwootAgent, setDeletingChatwootAgent] = useState(false);
  const [savingChatwootAgentName, setSavingChatwootAgentName] = useState(false);
  const [savingChatwootLink, setSavingChatwootLink] = useState(false);
  const chatwootAgentSyncRef = useRef(null);
  const blockedAgentUnlinkRef = useRef(false);

  const isAdmin = Boolean(user?.isAdmin);
  const isMobileView = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
  const isEditing = Boolean(form.id);
  const isCreateMode = !isEditing;
  const isReservedForN8n = isEditing && isReservedForN8nUser(form);
  const normalizedUser = String(form.user || '').trim();
  const normalizedCorreo = String(form.correo || '').trim();
  const normalizedTelefono = String(form.telefono || '').trim();
  const normalizedPassword = String(form.password || '').trim();
  const emailLooksValid = /^\S+@\S+\.\S+$/.test(normalizedCorreo);
  const needsPassword = isCreateMode;
  const canSubmit = !saving;
  const createUserDuplicate = useMemo(
    () =>
      findUserDuplicateConflict(users, {
        user: normalizedUser,
        correo: normalizedCorreo,
        excludeUserId: form.id
      }),
    [users, normalizedUser, normalizedCorreo, form.id]
  );
  const createUserNameTaken = Boolean(createUserDuplicate.userMatch);
  const createUserCorreoTaken = Boolean(createUserDuplicate.correoMatch);
  const roleOptions = useMemo(
    () =>
      [...roles].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' })
      ),
    [roles]
  );

  const selectedRoleIsMedico = useMemo(() => {
    const roleId = Array.isArray(form.roles) ? form.roles[0] : null;
    if (roleId == null || roleId === '') return false;
    const role = roleOptions.find((r) => Number(r.id) === Number(roleId));
    return isMedicoRoleName(role?.name);
  }, [roleOptions, form.roles]);

  const selectedRoleIsAdministrativo = useMemo(() => {
    const roleId = Array.isArray(form.roles) ? form.roles[0] : null;
    if (roleId == null || roleId === '') return false;
    const role = roleOptions.find((r) => Number(r.id) === Number(roleId));
    return isAdministrativoRoleName(role?.name);
  }, [roleOptions, form.roles]);

  const selectedRoleIsCallcenter = useMemo(() => {
    const roleId = Array.isArray(form.roles) ? form.roles[0] : null;
    if (roleId == null || roleId === '') return false;
    const role = roleOptions.find((r) => Number(r.id) === Number(roleId));
    return isCallcenterRoleName(role?.name);
  }, [roleOptions, form.roles]);

  const medicoLinksTakenByOther = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      const mid = Number(u.medico_id);
      if (Number.isFinite(mid) && mid > 0 && Number(u.id) !== Number(form.id)) {
        map.set(mid, u.user);
      }
    });
    return map;
  }, [users, form.id]);

  const administrativoLinksTakenByOther = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      const aid = Number(u.administrativo_id);
      if (Number.isFinite(aid) && aid > 0 && Number(u.id) !== Number(form.id)) {
        map.set(aid, u.user);
      }
    });
    return map;
  }, [users, form.id]);

  const callcenterLinksTakenByOther = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      const cid = Number(u.callcenter_id);
      if (Number.isFinite(cid) && cid > 0 && Number(u.id) !== Number(form.id)) {
        map.set(cid, u.user);
      }
    });
    return map;
  }, [users, form.id]);

  const userFormIsAdministrativoThreeStep = selectedRoleIsAdministrativo || selectedRoleIsCallcenter;
  const userFormStep2IsMedico = selectedRoleIsMedico;
  const userFormStep2Label = userFormStep2IsMedico ? 'Médico' : 'WhatsApp';
  const userFormStep3Label = selectedRoleIsCallcenter ? 'Call Center' : 'Administrativo';

  const canAdvanceUserFormStep1 =
    Boolean(normalizedUser) &&
    emailLooksValid &&
    Array.isArray(form.roles) &&
    form.roles.length > 0 &&
    !createUserNameTaken &&
    !createUserCorreoTaken;
  const canAdvanceUserMedicoStep2 = Boolean(String(form.medico_id || '').trim());
  const canAdvanceUserAdministrativoStep2 = Boolean(String(form.administrativo_id || '').trim());
  const canAdvanceCreateStep1 =
    canAdvanceUserFormStep1 && (!needsPassword || normalizedPassword.length >= 6);
  const canAdvanceEditStep1 = canAdvanceUserFormStep1;

  useEffect(() => {
    setEditFormStep(1);
  }, [form.id]);

  useEffect(() => {
    if (!isCreateModalOpen || createModalStep !== 1 || !selectedRoleIsMedico) return;
    if (!visibleMedicos.length && !visibleMedicosLoading) {
      loadVisibleMedicos().catch(() => { });
    }
  }, [isCreateModalOpen, createModalStep, selectedRoleIsMedico, visibleMedicos.length, visibleMedicosLoading]);

  useEffect(() => {
    if (!selectedRoleIsAdministrativo) return;
    const onAdminStep3 =
      (isCreateModalOpen && createModalStep === 3)
      || (Boolean(form.id) && editFormStep === 3);
    if (!onAdminStep3) return;
    if (!visibleAdministrativos.length && !visibleAdministrativosLoading) {
      loadVisibleAdministrativos().catch(() => { });
    }
  }, [
    selectedRoleIsAdministrativo,
    isCreateModalOpen,
    createModalStep,
    form.id,
    editFormStep,
    visibleAdministrativos.length,
    visibleAdministrativosLoading
  ]);

  useEffect(() => {
    if (!selectedRoleIsCallcenter) return;
    const onCallcenterStep3 =
      (isCreateModalOpen && createModalStep === 3)
      || (Boolean(form.id) && editFormStep === 3);
    if (!onCallcenterStep3) return;
    if (!visibleCallcenters.length && !visibleCallcentersLoading) {
      loadVisibleCallcenters().catch(() => { });
    }
  }, [
    selectedRoleIsCallcenter,
    isCreateModalOpen,
    createModalStep,
    form.id,
    editFormStep,
    visibleCallcenters.length,
    visibleCallcentersLoading
  ]);

  useEffect(() => {
    if (!selectedRoleIsAdministrativo && !selectedRoleIsCallcenter) return;
    const onStep3 =
      (isCreateModalOpen && createModalStep === 3)
      || (Boolean(form.id) && editFormStep === 3);
    if (!onStep3) return;
    if (!visibleCallcenters.length && !visibleCallcentersLoading) {
      loadVisibleCallcenters().catch(() => { });
    }
  }, [
    selectedRoleIsAdministrativo,
    selectedRoleIsCallcenter,
    isCreateModalOpen,
    createModalStep,
    form.id,
    editFormStep,
    visibleCallcenters.length,
    visibleCallcentersLoading
  ]);

  useEffect(() => {
    isCreateModalOpenRef.current = isCreateModalOpen;
  }, [isCreateModalOpen]);

  const pauseCreateModalForOverlay = () => {
    const dlg = createModalDialogRef.current;
    if (isCreateModalOpenRef.current && dlg?.open) dlg.close();
  };

  const resumeCreateModalAfterOverlay = () => {
    if (!isCreateModalOpenRef.current) return;
    const dlg = createModalDialogRef.current;
    if (dlg && !dlg.open) {
      try {
        dlg.showModal();
      } catch (_) {
        // ignore
      }
    }

  };

  const runWithCreateModalPaused = async (task) => {
    pauseCreateModalForOverlay();
    try {
      return await task();
    } finally {
      resumeCreateModalAfterOverlay();
    }
  };

  const editingUserRow = useMemo(
    () => (form.id ? users.find((u) => Number(u.id) === Number(form.id)) : null),
    [users, form.id]
  );
  const savedChatwootAgentId =
    editingUserRow?.chatwoot_agent_id != null && editingUserRow?.chatwoot_agent_id !== ''
      ? String(editingUserRow.chatwoot_agent_id)
      : '';
  const selectableChatwootAgents = useMemo(
    () =>
      filterSelectableChatwootAgents(
        chatwootAgents,
        users,
        form.id,
        form.chatwoot_agent_id || savedChatwootAgentId
      ),
    [chatwootAgents, users, form.id, form.chatwoot_agent_id, savedChatwootAgentId]
  );

  const mergeSavedUserIntoForm = (savedRow) => {
    if (!savedRow?.id) return;
    const cw = buildChatwootFieldsFromUser(savedRow, chatwootAgents);
    chatwootAgentSyncRef.current = cw.chatwoot_agent_id || null;
    setForm((p) => {
      if (Number(p.id) !== Number(savedRow.id)) return p;
      return {
        ...p,
        user: savedRow.user ?? p.user,
        correo: savedRow.correo ?? p.correo,
        telefono: savedRow.telefono ?? p.telefono,
        is_active: savedRow.is_active === 0 ? 0 : 1,
        chatwoot_agent_id: cw.chatwoot_agent_id,
        chatwoot_agent_name: cw.chatwoot_agent_name || p.chatwoot_agent_name,
        medico_id: savedRow.medico_id != null && savedRow.medico_id !== '' ? String(savedRow.medico_id) : '',
        medico_nombre: savedRow.medico_nombre || p.medico_nombre || '',
        administrativo_id: savedRow.administrativo_id != null && savedRow.administrativo_id !== ''
          ? String(savedRow.administrativo_id)
          : '',
        administrativo_nombre: savedRow.administrativo_nombre || p.administrativo_nombre || '',
        callcenter_id: savedRow.callcenter_id != null && savedRow.callcenter_id !== ''
          ? String(savedRow.callcenter_id)
          : '',
        callcenter_nombre: savedRow.callcenter_nombre || p.callcenter_nombre || ''
      };
    });
  };

  const filteredUsers = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const base = !q
      ? users
      : users.filter((u) =>
        [u.user, u.correo, u.telefono].some((v) => String(v || '').toLowerCase().includes(q))
      );
    return [...base].sort((a, b) => {
      const ra = roleSortRank(userRoleMap[a.id] || getRoleLabelFromUser(a));
      const rb = roleSortRank(userRoleMap[b.id] || getRoleLabelFromUser(b));
      if (ra !== rb) return ra - rb;
      return String(a.user || '').localeCompare(String(b.user || ''), 'es', { sensitivity: 'base' });
    });
  }, [users, query, userRoleMap]);

  const directoryGroupCounts = useMemo(() => {
    const counts = {};
    filteredUsers.forEach((u) => {
      const rank = roleSortRank(userRoleMap[u.id] || getRoleLabelFromUser(u));
      counts[rank] = (counts[rank] || 0) + 1;
    });
    return counts;
  }, [filteredUsers, userRoleMap]);

  const chatwootAgentNameById = useMemo(() => {
    const map = new Map();
    chatwootAgents.forEach((agent) => {
      const id = String(agent?.id || '').trim();
      if (id) map.set(id, String(agent?.name || '').trim());
    });
    return map;
  }, [chatwootAgents]);

  const directoryPageSizeNumber = useMemo(() => {
    if (directoryPageSize === 'all') return filteredUsers.length || 1;
    const n = Number(directoryPageSize);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [directoryPageSize, filteredUsers.length]);

  const directoryTotalPages = useMemo(() => {
    if (directoryPageSize === 'all') return 1;
    return Math.max(1, Math.ceil(filteredUsers.length / directoryPageSizeNumber));
  }, [filteredUsers.length, directoryPageSize, directoryPageSizeNumber]);

  const directoryPageSafe = Math.min(Math.max(1, directoryPage), directoryTotalPages);

  const paginatedDirectoryUsers = useMemo(() => {
    if (directoryPageSize === 'all') return filteredUsers;
    const start = (directoryPageSafe - 1) * directoryPageSizeNumber;
    return filteredUsers.slice(start, start + directoryPageSizeNumber);
  }, [filteredUsers, directoryPageSize, directoryPageSafe, directoryPageSizeNumber]);

  const directoryListMeta = useMemo(() => {
    const filtered = filteredUsers.length;
    const total = users.length;
    const trimmedQuery = String(query || '').trim();
    if (!filtered) {
      return trimmedQuery ? `0 de ${total}` : `0 usuarios`;
    }
    if (directoryPageSize === 'all') {
      return trimmedQuery ? `${filtered} de ${total}` : `${filtered} usuarios`;
    }
    const start = (directoryPageSafe - 1) * directoryPageSizeNumber + 1;
    const end = Math.min(filtered, start + directoryPageSizeNumber - 1);
    const range = `${start}-${end} de ${filtered}`;
    return trimmedQuery ? `${range} · ${total} total` : range;
  }, [
    filteredUsers.length,
    users.length,
    query,
    directoryPageSize,
    directoryPageSafe,
    directoryPageSizeNumber
  ]);

  const userStats = useMemo(() => {
    const active = users.filter((u) => Number(u.is_active) === 1).length;
    const withChatwoot = users.filter((u) => u.chatwoot_agent_id != null && u.chatwoot_agent_id !== '').length;
    return { total: users.length, active, withChatwoot, showing: filteredUsers.length };
  }, [users, filteredUsers]);

  useEffect(() => {
    setDirectoryPage(1);
  }, [query, directoryPageSize]);

  const fieldInputStyle = {
    width: '100%',
    boxSizing: 'border-box'
  };

  const editingRoleLabel = roleOptions.find((r) => form.roles.includes(Number(r.id)))?.name || '';

  const editingChatwootAgentSummary = useMemo(() => {
    if (selectedRoleIsMedico) return null;
    const agentId = String(form.chatwoot_agent_id || '').trim();
    if (!agentId) {
      return { linked: false, metaLabel: 'Sin agente vinculado' };
    }
    const agent = chatwootAgents.find((a) => String(a.id) === agentId);
    const name =
      String(form.chatwoot_agent_name || agent?.name || '').trim() || `Agente #${agentId}`;
    return {
      linked: true,
      metaLabel: `${name} (#${agentId})`
    };
  }, [selectedRoleIsMedico, form.chatwoot_agent_id, form.chatwoot_agent_name, chatwootAgents]);

  const editingPermissionItems = useMemo(
    () =>
      form.permissions
        .map((pid) => {
          const perm = permissions.find((p) => Number(p.id) === Number(pid));
          if (!perm) return null;
          return {
            id: perm.id,
            code: perm.code,
            label: formatPermissionLabel(perm.code)
          };
        })
        .filter(Boolean)
        .sort((a, b) => String(a.label).localeCompare(String(b.label), 'es', { sensitivity: 'base' })),
    [form.permissions, permissions]
  );

  const showToast = (message, type = 'info') => {
    Swal.fire({
      toast: true,
      position: 'bottom-end',
      text: message,
      showConfirmButton: false,
      timer: 3200,
      timerProgressBar: true,
      icon: type,
      customClass: { popup: 'swal-toast-crm' },
      background: '#fff'
    });
  };
  const loadChatwootAgents = async () => {
    setChatwootAgentsLoading(true);
    setChatwootAgentsError('');
    try {
      const [agentsRes, configRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/users/chatwoot-agents`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/admin/users/chatwoot-agents/config`, { credentials: 'include' })
      ]);
      const agentsJson = await agentsRes.json().catch(() => ({}));
      const configJson = await configRes.json().catch(() => ({}));
      if (!agentsRes.ok) throw new Error(agentsJson.error || 'No se pudieron cargar agentes de Chatwoot');
      setChatwootAgents(Array.isArray(agentsJson) ? agentsJson : []);
      setChatwootCreateConfig({
        create_without_invite: Boolean(configJson.create_without_invite),
        platform_configured: Boolean(configJson.platform_configured),
        password_valid: Boolean(configJson.password_valid),
        password_issues: Array.isArray(configJson.password_issues) ? configJson.password_issues : [],
        password_hint: String(configJson.password_hint || '')
      });
    } catch (e) {
      setChatwootAgents([]);
      setChatwootAgentsError(e.message || 'Error al cargar agentes');
    } finally {
      setChatwootAgentsLoading(false);
    }
  };

  const createChatwootAgentForForm = async () => {
    if (!normalizedUser) {
      showToast('Completá el nombre de usuario antes de crear el agente.', 'warning');
      return;
    }
    if (!emailLooksValid) {
      showToast('Completá un correo válido antes de crear el agente.', 'warning');
      return;
    }
    if (isBlockedChatwootAgentEmail(normalizedCorreo)) {
      showToast(
        'No podés crear ni vincular un agente con farmagsa@mail.com o farmagsa@gmail.com. Usá el correo del operador (ej. administrativo_1@mail.com).',
        'warning'
      );
      return;
    }
    const agentDisplayName = String(form.chatwoot_agent_name || '').trim();
    if (!agentDisplayName) {
      showToast('Ingresá el nombre visible en Chatwoot antes de crear el agente.', 'warning');
      return;
    }

    const setupHtml = (() => {
      if (chatwootCreateConfig.platform_configured && !chatwootCreateConfig.password_valid) {
        const issues = (chatwootCreateConfig.password_issues || []).join(', ');
        return `La variable <strong>CHATWOOT_NEW_AGENT_PASSWORD</strong> en pulse-back no cumple las reglas de Chatwoot${issues ? `: ${issues}` : ''}.<br><br>
          Si escribiste <code>FarmagsaAgent#2026</code> <strong>sin comillas</strong>, el <code>#</code> corta el valor en el .env y solo queda <code>FarmagsaAgent</code> (sin número ni símbolo).<br><br>
          Poné en pulse-back, por ejemplo:<br>
          <code>CHATWOOT_NEW_AGENT_PASSWORD="FarmagsaAgent$2026"</code><br><br>
          ${chatwootCreateConfig.password_hint ? `${chatwootCreateConfig.password_hint}<br><br>` : ''}
          Guardá, <strong>reiniciá pulse-back</strong> y volvé a intentar.`;
      }
      return `Faltan variables en el <strong>.env de pulse-back</strong>:<br><br>
        <strong>CHATWOOT_PLATFORM_APP_TOKEN</strong> — Chatwoot → Super Admin → Platform Apps → crear app → copiar token.<br>
        <strong>CHATWOOT_NEW_AGENT_PASSWORD</strong> — ej. <code>CHATWOOT_NEW_AGENT_PASSWORD="FarmagsaAgent$2026"</code> (con comillas si usás <code>#</code>).<br><br>
        Reiniciá pulse-back después de guardar.`;
    })();

    const confirmCreate = await runWithCreateModalPaused(() =>
      fireConfirmDialog({
        title: chatwootCreateWithoutInvite ? 'Crear agente' : 'Configuración pendiente',
        html: chatwootCreateWithoutInvite
          ? buildCrmSwalBody({
            lead: `Agente <strong>${agentDisplayName}</strong> · <strong>${normalizedCorreo}</strong>`,
            items: [
              'Activo de inmediato (sin invitación)',
              `Vinculado al usuario <strong>${normalizedUser}</strong>`,
              'Atiende WhatsApp desde el CRM'
            ]
          })
          : wrapCrmSwalRich(setupHtml),
        icon: chatwootCreateWithoutInvite ? 'question' : 'warning',
        confirmButtonText: chatwootCreateWithoutInvite ? 'Crear agente' : 'Entendido',
        showCancelButton: chatwootCreateWithoutInvite,
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false
      })
    );
    if (!chatwootCreateWithoutInvite) return;
    if (!confirmCreate.isConfirmed) return;

    setCreatingChatwootAgent(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/chatwoot-agents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: agentDisplayName,
          email: normalizedCorreo,
          role: 'agent'
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'No se pudo crear el agente en Chatwoot');

      await loadChatwootAgents();

      const newAgentId = json.id != null ? String(json.id) : '';
      const newAgentName = json.name || agentDisplayName;

      if (newAgentId) {
        chatwootAgentSyncRef.current = newAgentId;
        setForm((p) => ({
          ...p,
          chatwoot_agent_id: newAgentId,
          chatwoot_agent_name: newAgentName
        }));
        if (form.id) {
          const saved = await persistChatwootAgentLink(form.id, newAgentId);
          setUsers((prev) =>
            prev.map((u) =>
              Number(u.id) === Number(form.id)
                ? { ...u, chatwoot_agent_id: saved.chatwoot_agent_id ?? Number(newAgentId) }
                : u
            )
          );
        }
      }

      if (json.existing) {
        showToast(`Ya existía en Chatwoot: «${json.name}». Vinculado automáticamente.`, 'success');
      } else {
        showToast(
          `Agente «${newAgentName}» creado y activo en Chatwoot. Ya puede atender conversaciones desde el CRM.`,
          'success'
        );
      }
      loadAll().catch(() => { });
    } catch (e) {
      showToast(e.message || 'Error al crear agente en Chatwoot', 'error');
    } finally {
      setCreatingChatwootAgent(false);
    }
  };

  const persistChatwootAgentLink = async (userId, agentId) => {
    const normalized =
      agentId === '' || agentId == null || agentId === undefined ? null : Number(agentId);
    const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/chatwoot-agent`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chatwoot_agent_id: normalized })
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json.error || 'No se pudo guardar el vínculo con Chatwoot');
    }
    return json;
  };

  const applyChatwootLinkFromServer = (savedRow) => {
    if (!savedRow?.id) return;
    const cw = buildChatwootFieldsFromUser(savedRow, chatwootAgents);
    chatwootAgentSyncRef.current = cw.chatwoot_agent_id || null;
    setForm((p) => (Number(p.id) === Number(savedRow.id) ? { ...p, ...cw } : p));
    setUsers((prev) =>
      prev.map((u) =>
        Number(u.id) === Number(savedRow.id)
          ? { ...u, chatwoot_agent_id: savedRow.chatwoot_agent_id ?? null }
          : u
      )
    );
  };

  const handleChatwootAgentIdChange = async (agentId) => {
    const editingUserId = form.id;
    const picked = agentId
      ? chatwootAgents.find((a) => String(a.id) === String(agentId))
      : null;

    if (agentId && isBlockedChatwootAgent(picked)) {
      showToast(
        `El agente ${blockedChatwootAgentLabel(picked)} no se puede asignar (cuenta admin Chatwoot). Usá Liliana o Verónica.`,
        'warning'
      );
      return;
    }

    if (!agentId && editingUserId && form.chatwoot_agent_id) {
      const currentAgent =
        chatwootAgents.find((a) => String(a.id) === String(form.chatwoot_agent_id)) || null;
      if (!isBlockedChatwootAgent(currentAgent)) {
        const agentLabel = currentAgent?.name || `agente #${form.chatwoot_agent_id}`;
        const confirmUnlink = await runWithCreateModalPaused(() =>
          fireConfirmDialog({
            title: 'Quitar vínculo',
            html: buildCrmSwalBody({
              lead: `Desvinculás <strong>${normalizedUser || 'este usuario'}</strong> de <strong>${agentLabel}</strong>.`,
              items: ['El agente sigue en Chatwoot', 'Dejará de usarse en el CRM']
            }),
            icon: 'warning',
            confirmButtonText: 'Quitar vínculo',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: false
          })
        );
        if (!confirmUnlink.isConfirmed) return;
      }
    }

    setForm((p) => ({
      ...p,
      chatwoot_agent_id: agentId,
      chatwoot_agent_name: picked?.name || ''
    }));

    if (!editingUserId) return;

    setSavingChatwootLink(true);
    try {
      const saved = await persistChatwootAgentLink(editingUserId, agentId);
      applyChatwootLinkFromServer(saved);
      showToast(
        agentId
          ? `Vínculo guardado: agente #${agentId} en la base de datos.`
          : 'Vínculo con Chatwoot quitado.',
        'success'
      );
      loadAll().catch(() => { });
    } catch (e) {
      showToast(
        e.message ||
        'No se guardó el vínculo. Reiniciá pulse-back con el código actualizado.',
        'error'
      );
      const row = users.find((u) => Number(u.id) === Number(editingUserId));
      if (row) applyChatwootLinkFromServer(row);
    } finally {
      setSavingChatwootLink(false);
    }
  };

  const deleteChatwootAgentForForm = async () => {
    const agentId = form.chatwoot_agent_id;
    if (!agentId) {
      showToast('No hay agente de Chatwoot vinculado.', 'warning');
      return;
    }

    const selected =
      chatwootAgents.find((a) => String(a.id) === String(agentId)) || null;
    const agentLabel =
      selected?.name || String(form.chatwoot_agent_name || '').trim() || `Agente #${agentId}`;
    const linkedCrmUsers = (users || []).filter(
      (u) => String(u.chatwoot_agent_id || '') === String(agentId)
    );
    const otherLinked = linkedCrmUsers.filter(
      (u) => form.id == null || Number(u.id) !== Number(form.id)
    );
    const linkedNames = linkedCrmUsers
      .map((u) => String(u.user || u.username || '').trim() || `usuario #${u.id}`)
      .filter(Boolean);

    const deleteItems = [
      linkedNames.length
        ? `Vínculo CRM: ${linkedNames.join(', ')}`
        : 'Se quita el vínculo en el CRM',
      'Borrado permanente en Chatwoot'
    ];
    if (otherLinked.length > 0) {
      deleteItems.push(
        `También desvincula: ${otherLinked
          .map((u) => String(u.user || '').trim() || `#${u.id}`)
          .join(', ')}`
      );
    }

    const confirmDelete = await runWithCreateModalPaused(() =>
      fireConfirmDialog({
        title: 'Eliminar agente',
        html: buildCrmSwalBody({
          lead: `Se elimina <strong>${agentLabel}</strong> (#${agentId})${selected?.email ? ` · ${selected.email}` : ''
            }.`,
          items: deleteItems,
          footnote: 'Esta acción no se puede deshacer.'
        }),
        icon: 'error',
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc2626',
        allowOutsideClick: false
      })
    );
    if (!confirmDelete.isConfirmed) return;

    setDeletingChatwootAgent(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/chatwoot-agents/${agentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'No se pudo eliminar el agente en Chatwoot');

      await loadChatwootAgents();
      chatwootAgentSyncRef.current = null;
      setForm((p) => ({ ...p, chatwoot_agent_id: '', chatwoot_agent_name: '' }));

      if (form.id) {
        const saved = await persistChatwootAgentLink(form.id, '');
        applyChatwootLinkFromServer(saved);
      } else {
        setUsers((prev) =>
          prev.map((u) =>
            String(u.chatwoot_agent_id || '') === String(agentId)
              ? { ...u, chatwoot_agent_id: null }
              : u
          )
        );
      }

      showToast(`Agente «${agentLabel}» eliminado de Chatwoot.`, 'success');
      loadAll().catch(() => { });
    } catch (e) {
      showToast(e.message || 'Error al eliminar agente en Chatwoot', 'error');
    } finally {
      setDeletingChatwootAgent(false);
    }
  };

  const saveChatwootAgentName = async () => {
    const agentId = form.chatwoot_agent_id;
    const name = String(form.chatwoot_agent_name || '').trim();
    if (!agentId) {
      showToast('Elegí un agente de Chatwoot primero.', 'warning');
      return;
    }
    if (!name) {
      showToast('Ingresá un nombre para el agente.', 'warning');
      return;
    }

    const currentName =
      chatwootAgents.find((a) => String(a.id) === String(agentId))?.name || 'sin nombre';
    const confirmUpdate = await runWithCreateModalPaused(() =>
      fireConfirmDialog({
        title: 'Actualizar nombre',
        html: buildCrmSwalBody({
          lead: `Agente #${agentId}: <strong>${currentName}</strong> → <strong>${name}</strong>`,
          items: ['Los clientes verán el nuevo nombre en WhatsApp']
        }),
        icon: 'question',
        confirmButtonText: 'Aplicar nombre',
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false
      })
    );
    if (!confirmUpdate.isConfirmed) return;

    setSavingChatwootAgentName(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/chatwoot-agents/${agentId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'No se pudo actualizar el nombre');

      await loadChatwootAgents();
      chatwootAgentSyncRef.current = String(agentId);
      setForm((p) => ({ ...p, chatwoot_agent_name: json.name || name }));
      showToast('Nombre actualizado en Chatwoot.', 'success');
    } catch (e) {
      showToast(e.message || 'Error al guardar nombre', 'error');
    } finally {
      setSavingChatwootAgentName(false);
    }
  };

  const loadVisibleMedicos = async () => {
    setVisibleMedicosLoading(true);
    setVisibleMedicosError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/medicos`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.medicos) ? json.medicos : [];
      setVisibleMedicos(
        [...list].sort((a, b) =>
          String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es', { sensitivity: 'base' })
        )
      );
    } catch (e) {
      setVisibleMedicos([]);
      setVisibleMedicosError(e.message || 'No se pudo cargar el catálogo de médicos visibles.');
    } finally {
      setVisibleMedicosLoading(false);
    }
  };

  const loadVisibleAdministrativos = async () => {
    setVisibleAdministrativosLoading(true);
    setVisibleAdministrativosError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/administrativos`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.administrativos) ? json.administrativos : [];
      setVisibleAdministrativos(
        [...list].sort((a, b) =>
          String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es', { sensitivity: 'base' })
        )
      );
    } catch (e) {
      setVisibleAdministrativos([]);
      setVisibleAdministrativosError(e.message || 'No se pudo cargar el catálogo de administrativos visibles.');
    } finally {
      setVisibleAdministrativosLoading(false);
    }
  };

  const loadVisibleCallcenters = async () => {
    setVisibleCallcentersLoading(true);
    setVisibleCallcentersError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/callcenter/visibilidad`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.callcenters) ? json.callcenters : [];
      setVisibleCallcenters(
        [...list].sort((a, b) =>
          String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es', { sensitivity: 'base' })
        )
      );
    } catch (e) {
      setVisibleCallcenters([]);
      setVisibleCallcentersError(e.message || 'No se pudo cargar el catálogo de callcenter visibles.');
    } finally {
      setVisibleCallcentersLoading(false);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const visibleCatalogPromise = Promise.all([
        loadVisibleMedicos(),
        loadVisibleAdministrativos()
      ]);
      const [uRes, rRes, pRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/users`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/admin/roles`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/admin/permissions`, { credentials: 'include' })
      ]);
      await visibleCatalogPromise;

      const [uJson, rJson, pJson] = await Promise.all([
        uRes.json().catch(() => []),
        rRes.json().catch(() => []),
        pRes.json().catch(() => [])
      ]);

      if (!uRes.ok) throw new Error(uJson.error || 'No se pudo cargar usuarios');
      if (!rRes.ok) throw new Error(rJson.error || 'No se pudo cargar roles');
      if (!pRes.ok) throw new Error(pJson.error || 'No se pudo cargar permisos');

      setUsers(Array.isArray(uJson) ? uJson : []);
      setRoles(Array.isArray(rJson) ? rJson : []);
      setPermissions(Array.isArray(pJson) ? pJson : []);
    } catch (e) {
      setError(e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadAll();
    loadChatwootAgents();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || loading || !users.length || !chatwootAgents.length) return;
    if (blockedAgentUnlinkRef.current) return;

    const blockedIds = new Set(
      chatwootAgents.filter((a) => isBlockedChatwootAgent(a)).map((a) => String(a.id))
    );
    if (!blockedIds.size) return;

    const toUnlink = users.filter((u) => blockedIds.has(String(u.chatwoot_agent_id || '')));
    if (!toUnlink.length) return;

    blockedAgentUnlinkRef.current = true;
    Promise.all(toUnlink.map((u) => persistChatwootAgentLink(u.id, null)))
      .then(() => {
        const labels = toUnlink
          .map((u) => {
            const agent = chatwootAgents.find((a) => String(a.id) === String(u.chatwoot_agent_id));
            return `${u.user} → ${blockedChatwootAgentLabel(agent)}`;
          })
          .join(', ');
        showToast(
          `Se quitó el vínculo con agente bloqueado (${labels}). Asigná Liliana o Verónica en cada usuario.`,
          'info'
        );
        return loadAll();
      })
      .catch((e) => {
        blockedAgentUnlinkRef.current = false;
        showToast(e.message || 'No se pudo quitar el agente bloqueado', 'error');
      });
  }, [isAdmin, loading, users, chatwootAgents]);

  useEffect(() => {
    if (!form.id || !form.chatwoot_agent_id || !chatwootAgents.length) return;
    const picked = chatwootAgents.find((a) => String(a.id) === String(form.chatwoot_agent_id));
    if (!isBlockedChatwootAgent(picked)) return;
    let cancelled = false;
    (async () => {
      try {
        const saved = await persistChatwootAgentLink(form.id, null);
        if (!cancelled) applyChatwootLinkFromServer(saved);
      } catch (_) {
        /* silent; el panel muestra el agente bloqueado hasta recargar */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.id, form.chatwoot_agent_id, chatwootAgents]);

  useEffect(() => {
    const agentId = form.chatwoot_agent_id ? String(form.chatwoot_agent_id) : '';
    if (!agentId || !chatwootAgents.length) return;
    if (agentId === chatwootAgentSyncRef.current) return;
    const picked = chatwootAgents.find((a) => String(a.id) === agentId);
    chatwootAgentSyncRef.current = agentId;
    if (picked?.name) {
      setForm((p) => ({ ...p, chatwoot_agent_name: picked.name }));
    }
  }, [chatwootAgents, form.chatwoot_agent_id]);

  useEffect(() => {
    if (!users.length) {
      setUserRoleMap({});
      return;
    }
    let cancelled = false;
    const loadUserRoles = async () => {
      try {
        const entries = await Promise.all(
          users.map(async (u) => {
            try {
              const res = await fetch(`${API_BASE_URL}/api/admin/users/${u.id}/roles`, { credentials: 'include' });
              const json = await res.json().catch(() => []);
              if (!res.ok) return [u.id, getRoleLabelFromUser(u)];
              const firstRole = (Array.isArray(json) ? json : [])[0];
              return [u.id, firstRole?.name || getRoleLabelFromUser(u)];
            } catch (_) {
              return [u.id, getRoleLabelFromUser(u)];
            }
          })
        );
        if (!cancelled) {
          setUserRoleMap(Object.fromEntries(entries));
        }
      } catch (_) {
        if (!cancelled) setUserRoleMap({});
      }
    };
    loadUserRoles();
    return () => {
      cancelled = true;
    };
  }, [users]);

  useEffect(() => {
    if (!previewPhoto) return undefined;
    const onKeyDown = (evt) => {
      if (evt.key === 'Escape') setPreviewPhoto('');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewPhoto]);

  const startCreate = () => {
    setError('');
    setSuccess('');
    setForm(emptyForm());
    setExpandedRpPanel(null);
    setCreateModalStep(1);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    const dlg = createModalDialogRef.current;
    if (dlg?.open) {
      dlg.close();
    }
    setCreateModalStep(1);
    setIsCreateModalOpen(false);
  };

  const validateUserFormStep1 = ({ requirePassword = false } = {}) => {
    if (!normalizedUser) {
      showToast('Ingresá un nombre de usuario.', 'warning');
      return false;
    }
    if (!emailLooksValid) {
      showToast('Ingresá un correo válido.', 'warning');
      return false;
    }
    if (requirePassword && normalizedPassword.length < 6) {
      showToast('La contraseña inicial debe tener al menos 6 caracteres.', 'warning');
      return false;
    }
    if (!Array.isArray(form.roles) || form.roles.length === 0) {
      showToast('Seleccioná un rol para continuar.', 'warning');
      return false;
    }
    if (createUserNameTaken) {
      const taken = createUserDuplicate.userMatch?.user || normalizedUser;
      showToast(`El usuario «${taken}» ya está registrado. Elegí otro nombre.`, 'warning');
      return false;
    }
    if (createUserCorreoTaken) {
      const taken = createUserDuplicate.correoMatch?.correo || normalizedCorreo;
      showToast(`El correo «${taken}» ya está registrado. Usá otro correo.`, 'warning');
      return false;
    }
    return true;
  };

  const goUserFormStep2 = (setStep) => {
    if (!validateUserFormStep1({ requirePassword: needsPassword })) return;
    if (selectedRoleIsMedico) {
      if (!visibleMedicos.length && !visibleMedicosLoading) {
        loadVisibleMedicos().catch(() => { });
      }
    } else if (!chatwootAgents.length && !chatwootAgentsLoading) {
      loadChatwootAgents().catch(() => { });
    }
    setStep(2);
  };

  const goUserFormStep3 = (setStep) => {
    if (!visibleAdministrativos.length && !visibleAdministrativosLoading) {
      loadVisibleAdministrativos().catch(() => { });
    }
    setStep(3);
  };

  const goCreateModalStep2 = () => goUserFormStep2(setCreateModalStep);
  const goCreateModalStep3 = () => goUserFormStep3(setCreateModalStep);
  const goEditFormStep2 = () => goUserFormStep2(setEditFormStep);
  const goEditFormStep3 = () => goUserFormStep3(setEditFormStep);

  const handleCreateModalFormSubmit = (e) => {
    e.preventDefault();
    if (createModalStep === 1) {
      goCreateModalStep2();
      return;
    }
    if (createModalStep === 2 && userFormIsAdministrativoThreeStep) {
      goCreateModalStep3();
      return;
    }
    submit(e);
  };

  const handleEditFormSubmit = (e) => {
    e.preventDefault();
    if (!isReservedForN8n && editFormStep === 1) {
      goEditFormStep2();
      return;
    }
    if (!isReservedForN8n && editFormStep === 2 && userFormIsAdministrativoThreeStep) {
      goEditFormStep3();
      return;
    }
    submit(e);
  };

  useLayoutEffect(() => {
    if (!isCreateModalOpen) return undefined;
    const dlg = createModalDialogRef.current;
    if (!dlg || dlg.open) return undefined;
    try {
      dlg.showModal();
    } catch (_) {
      // ignore if already open
    }
    return () => {
      if (dlg.open) dlg.close();
    };
  }, [isCreateModalOpen]);

  const toggleRpPanel = (panel) => {
    setExpandedRpPanel((current) => (current === panel ? null : panel));
  };

  const clearUserSelection = () => {
    setIsCreateModalOpen(false);
    setEditFormStep(1);
    setExpandedRpPanel(null);
    chatwootAgentSyncRef.current = null;
    setForm(emptyForm());
    setError('');
    setSuccess('');
  };

  const requestDeleteUser = async (userRow, evt) => {
    evt?.stopPropagation?.();
    if (!userRow?.id || isReservedForN8nUser(userRow)) return;

    const roleLabel = userRoleMap[userRow.id] || getRoleLabelFromUser(userRow);

    if (isAdminRoleLabel(roleLabel)) {
      const msg = 'Los usuarios con rol admin no se pueden eliminar.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    const details = [
      { label: 'Usuario', value: userRow.user || '—' },
      { label: 'Rol', value: roleLabel || '—' }
    ];
    if (userRow.correo) details.push({ label: 'Correo', value: userRow.correo });

    // Paso 1 — Aviso y detalles de la cuenta.
    const step1 = await fireConfirmDialog({
      title: 'Eliminar usuario',
      html: `${buildCrmSwalSteps(1)}${buildCrmSwalBody({
        lead: 'Se quitará del CRM y no podrá iniciar sesión. Esta acción no se puede deshacer.',
        details,
        items: [
          'Se eliminan roles y permisos asignados',
          'No se borra el agente de Chatwoot en Chatwoot (solo el vínculo en Pulse)'
        ],
        footnote: 'Confirmá solo si estás seguro de que esta cuenta ya no se usa.'
      })}`,
      icon: 'warning',
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Cancelar',
      width: 'min(440px, 92vw)'
    });
    if (!step1.isConfirmed) return;

    // Paso 2 — Confirmación por contraseña de administrador.
    const step2 = await firePasswordConfirmDialog({
      title: 'Confirmá la eliminación',
      stepsHtml: buildCrmSwalSteps(2),
      lead: `Estás por eliminar a <strong>${userRow.user}</strong>. Ingresá la contraseña de administrador para continuar.`,
      hint: 'Esta acción no se puede deshacer.',
      expectedPassword: DELETE_USER_CONFIRM_PASSWORD,
      confirmButtonText: 'Eliminar usuario',
      cancelButtonText: 'Cancelar',
      width: 'min(420px, 92vw)'
    });
    if (!step2.isConfirmed) return;

    setDeletingUserId(Number(userRow.id));
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${userRow.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'No se pudo eliminar el usuario');

      if (Number(form.id) === Number(userRow.id)) {
        clearUserSelection();
      }
      await loadAll();
      const msg = `Usuario «${userRow.user}» eliminado.`;
      setSuccess(msg);
      showToast(msg, 'success');
    } catch (e) {
      const msg = e.message || 'No se pudo eliminar el usuario.';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setDeletingUserId(null);
    }
  };

  const startEdit = async (u) => {
    setIsCreateModalOpen(false);
    setEditFormStep(1);
    setExpandedRpPanel(null);
    const cw = buildChatwootFieldsFromUser(u, chatwootAgents);
    chatwootAgentSyncRef.current = cw.chatwoot_agent_id || null;
    setForm({
      id: u.id,
      user: u.user || '',
      correo: u.correo || '',
      telefono: u.telefono || '',
      is_active: u.is_active === 0 ? 0 : 1,
      chatwoot_agent_id: cw.chatwoot_agent_id,
      chatwoot_agent_name: cw.chatwoot_agent_name,
      medico_id: u.medico_id != null && u.medico_id !== '' ? String(u.medico_id) : '',
      medico_nombre: u.medico_nombre || '',
      administrativo_id: u.administrativo_id != null && u.administrativo_id !== '' ? String(u.administrativo_id) : '',
      administrativo_nombre: u.administrativo_nombre || '',
      callcenter_id: u.callcenter_id != null && u.callcenter_id !== '' ? String(u.callcenter_id) : '',
      callcenter_nombre: u.callcenter_nombre || '',
      password: '',
      roles: [],
      permissions: []
    });
    try {
      const [rr] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/users/${u.id}/roles`, { credentials: 'include' }),
      ]);
      const [rj] = await Promise.all([rr.json().catch(() => [])]);
      if (rr.ok) {
        const pickedRole = (Array.isArray(rj) ? rj : [])
          .map((x) => Number(x.id))
          .find((id) => roleOptions.some((opt) => Number(opt.id) === Number(id)));
        if (pickedRole) {
          await selectRoleAndSyncPermissions(pickedRole);
        } else {
          setForm((prev) => ({ ...prev, roles: [], permissions: [] }));
        }
      }
    } catch (_) { }
  };

  const selectRoleAndSyncPermissions = async (roleId, options = {}) => {
    const selectedRoleName = String(
      roleOptions.find((opt) => Number(opt.id) === Number(roleId))?.name || ''
    ).trim().toLowerCase();
    setForm((prev) => ({
      ...prev,
      roles: [Number(roleId)],
      ...(isMedicoRoleName(selectedRoleName)
        ? { administrativo_id: '', administrativo_nombre: '', callcenter_id: '', callcenter_nombre: '' }
        : isAdministrativoRoleName(selectedRoleName)
          ? { medico_id: '', medico_nombre: '', callcenter_id: '', callcenter_nombre: '' }
          : isCallcenterRoleName(selectedRoleName)
            ? { medico_id: '', medico_nombre: '', administrativo_id: '', administrativo_nombre: '' }
            : { medico_id: '', medico_nombre: '', administrativo_id: '', administrativo_nombre: '', callcenter_id: '', callcenter_nombre: '' })
    }));
    if (options.autoShowPermissions) {
      setExpandedRpPanel('permissions');
    }
    setSyncingRolePerms(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/roles/${roleId}/permissions`, { credentials: 'include' });
      const json = await res.json().catch(() => []);
      if (!res.ok) return;
      const rolePermIds = (Array.isArray(json) ? json : []).map((x) => Number(x.id));
      const forceAdminCodes = selectedRoleName === 'admin'
        ? ['ver_gestion_derivacion', 'ver_bot_farmacia']
        : [];
      const forceAdminIds = permissions
        .filter((p) => forceAdminCodes.includes(String(p.code || '').trim().toLowerCase()))
        .map((p) => Number(p.id));
      const mergedPermissions = [...new Set([...rolePermIds, ...forceAdminIds])];
      setForm((prev) => ({ ...prev, permissions: mergedPermissions }));
    } catch (_) {
      // silent; keep current permissions on network failure
    } finally {
      setSyncingRolePerms(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSuccess('');
    if (isReservedForN8n) {
      const msg = 'Este usuario está reservado para n8n y no puede editarse desde esta vista.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (!normalizedUser) {
      const msg = 'Ingresá un nombre de usuario.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (!normalizedCorreo || !emailLooksValid) {
      const msg = 'Ingresá un correo válido.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (needsPassword && normalizedPassword.length < 6) {
      const msg = 'La contraseña inicial debe tener al menos 6 caracteres.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (!Array.isArray(form.roles) || form.roles.length === 0) {
      const msg = 'Seleccioná un rol para continuar.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (selectedRoleIsMedico && !String(form.medico_id || '').trim()) {
      const msg = 'Seleccioná el médico visible del catálogo para vincular este usuario.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (selectedRoleIsAdministrativo && !String(form.administrativo_id || '').trim()) {
      const msg = 'Seleccioná el administrativo visible del catálogo para vincular este usuario.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (selectedRoleIsCallcenter && !String(form.callcenter_id || '').trim()) {
      const msg = 'Seleccioná la persona de Call Center visible del catálogo para vincular este usuario.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (createUserNameTaken) {
      const msg = `El usuario «${createUserDuplicate.userMatch?.user || normalizedUser}» ya existe.`;
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (createUserCorreoTaken) {
      const msg = `El correo «${createUserDuplicate.correoMatch?.correo || normalizedCorreo}» ya está en uso.`;
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    const pickedAgent = form.chatwoot_agent_id
      ? chatwootAgents.find((a) => String(a.id) === String(form.chatwoot_agent_id))
      : null;
    if (!selectedRoleIsMedico && form.chatwoot_agent_id && isBlockedChatwootAgent(pickedAgent)) {
      const msg = `No se puede guardar el agente ${blockedChatwootAgentLabel(pickedAgent)} (bloqueado en el CRM).`;
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (form.id && !selectedRoleIsMedico) {
        try {
          const linkSaved = await persistChatwootAgentLink(form.id, form.chatwoot_agent_id);
          mergeSavedUserIntoForm(linkSaved);
        } catch (linkErr) {
          throw new Error(
            linkErr.message ||
            'No se pudo guardar el agente Chatwoot. Reiniciá pulse-back e intentá de nuevo.'
          );
        }
      }

      const payload = {
        user: normalizedUser,
        correo: normalizedCorreo,
        telefono: normalizedTelefono,
        is_active: Number(form.is_active) === 0 ? 0 : 1,
        chatwoot_agent_id: selectedRoleIsMedico
          ? null
          : (String(form.chatwoot_agent_id || '').trim() === ''
            ? null
            : Number(form.chatwoot_agent_id)),
        roles: form.roles.slice(0, 1),
        permissions: form.permissions,
        medico_id: selectedRoleIsMedico
          ? (String(form.medico_id || '').trim() === '' ? null : Number(form.medico_id))
          : null,
        administrativo_id: selectedRoleIsAdministrativo
          ? (String(form.administrativo_id || '').trim() === '' ? null : Number(form.administrativo_id))
          : null,
        callcenter_id: selectedRoleIsCallcenter
          ? (String(form.callcenter_id || '').trim() === '' ? null : Number(form.callcenter_id))
          : null
      };
      if (!form.id || normalizedPassword) payload.password = normalizedPassword;

      const url = form.id
        ? `${API_BASE_URL}/api/admin/users/${form.id}`
        : `${API_BASE_URL}/api/admin/users`;
      const method = form.id ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'No se pudo guardar');
      await loadAll();
      if (!form.id) {
        const msg = 'Usuario creado correctamente.';
        setSuccess(msg);
        showToast(msg, 'success');
        if (json?.id) {
          mergeSavedUserIntoForm(json);
          closeCreateModal();
        } else {
          setForm(emptyForm());
          closeCreateModal();
        }
      } else {
        mergeSavedUserIntoForm(json);
        const msg = normalizedPassword
          ? 'Usuario y contraseña actualizados correctamente.'
          : 'Usuario actualizado correctamente.';
        setSuccess(msg);
        showToast(msg, 'success');
      }
    } catch (e2) {
      const msg = e2.message || 'No se pudo guardar';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="crm-au-page">
        <div className="crm-au-alert crm-au-alert--error" role="alert">
          No tenés permisos para administrar usuarios.
        </div>
      </div>
    );
  }

  return (
    <div className="crm-au-page">
      <header className="crm-au-page__head">
        <div className="crm-au-page__head-main">
          <div className="crm-au-page__icon" aria-hidden="true">
            <UiIcon name="users" size={22} />
          </div>
          <div>
            <h1 className="crm-au-page__title">Usuarios</h1>
            <p className="crm-au-page__sub">
              Alta, edición, roles y vínculo con operadores de Chatwoot para WhatsApp.
            </p>
          </div>
        </div>
        <div className="crm-au-page__head-actions">
          <div className="crm-au-kpi-row" aria-label="Resumen">
            <span className="crm-au-kpi crm-au-kpi--accent">
              Total <strong>{userStats.total}</strong>
            </span>
            <span className="crm-au-kpi">
              Activos <strong>{userStats.active}</strong>
            </span>
            <span className="crm-au-kpi">
              Con Chatwoot <strong>{userStats.withChatwoot}</strong>
            </span>
          </div>
          <button type="button" className="crm-profile-primary-btn crm-au-btn-new" onClick={startCreate}>
            <UiIcon name="plus" size={16} />
            Nuevo usuario
          </button>
        </div>
      </header>

      {error ? (
        <div className="crm-au-alert crm-au-alert--error" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="crm-au-alert crm-au-alert--success" role="status">
          {success}
        </div>
      ) : null}

      <div className="crm-admin-users-grid">
        <section className="crm-admin-users-panel">
          <div className="crm-au-panel-head">
            <span className="crm-au-panel-head__title">Directorio</span>
            <span className="crm-au-panel-head__meta">{directoryListMeta}</span>
          </div>
          <div className="crm-au-directory-toolbar">
            <div className="crm-au-search-wrap crm-au-search-wrap--toolbar">
              <span className="crm-au-search-icon" aria-hidden="true">
                <UiIcon name="search" size={16} />
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar usuario, correo o teléfono…"
                className="crm-profile-input crm-admin-users-search"
                aria-label="Buscar usuarios"
                type="text"
                name="crm-directory-search"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
              />
            </div>
            <label className="crm-au-directory-page-size">
              <span className="crm-au-directory-page-size__label">Mostrar</span>
              <span
                className={`crm-au-directory-page-size__control${directoryPageSizeOpen ? ' is-open' : ''
                  }`}
              >
                <select
                  className="crm-au-directory-page-size__select"
                  value={directoryPageSize}
                  onChange={(e) => setDirectoryPageSize(e.target.value)}
                  onFocus={() => setDirectoryPageSizeOpen(true)}
                  onBlur={() => setDirectoryPageSizeOpen(false)}
                  onClick={() => setDirectoryPageSizeOpen(true)}
                  aria-label="Cantidad de usuarios por página"
                >
                  {DIRECTORY_PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="crm-au-directory-page-size__chevron" aria-hidden="true" />
              </span>
            </label>
          </div>
          <div className="crm-admin-users-list">
            {loading ? (
              <div className="crm-admin-rp-roles-list__state">
                <div className="crm-admin-rp-loading">
                  <span className="crm-admin-rp-loading-spinner" aria-hidden="true" />
                  <span>Cargando usuarios…</span>
                </div>
              </div>
            ) : null}
            {!loading && filteredUsers.length === 0 ? (
              <div
                className={`crm-au-panel-empty crm-au-panel-empty--inline${String(query || '').trim() ? ' crm-au-panel-empty--search' : ''
                  }`}
              >
                <div className="crm-au-panel-empty__icon" aria-hidden="true">
                  <span className="crm-au-panel-empty__icon-ring" />
                  <span className="crm-au-panel-empty__icon-glyph">
                    <UiIcon name={String(query || '').trim() ? 'search' : 'users'} size={20} />
                  </span>
                </div>
                <h3 className="crm-au-panel-empty__title">
                  {String(query || '').trim()
                    ? (
                      <>
                        Sin resultados para «<span className="crm-au-panel-empty__query">{query.trim()}</span>»
                      </>
                    )
                    : 'No hay usuarios cargados'}
                </h3>
                <p className="crm-au-panel-empty__copy">
                  {String(query || '').trim()
                    ? 'Probá con otro nombre, correo o teléfono.'
                    : 'Creá el primer usuario con el botón Nuevo usuario.'}
                </p>
                {String(query || '').trim() ? (
                  <button
                    type="button"
                    className="crm-au-panel-empty__action"
                    onClick={() => setQuery('')}
                  >
                    Limpiar búsqueda
                  </button>
                ) : null}
              </div>
            ) : null}
            {paginatedDirectoryUsers.map((u, idx) => {
              const roleLabel = userRoleMap[u.id] || getRoleLabelFromUser(u);
              const groupRank = roleSortRank(roleLabel);
              const prevU = idx > 0 ? paginatedDirectoryUsers[idx - 1] : null;
              const prevRank = prevU
                ? roleSortRank(userRoleMap[prevU.id] || getRoleLabelFromUser(prevU))
                : null;
              const showGroupHeader = groupRank !== prevRank;
              const groupMeta = ROLE_GROUP_META[groupRank] || ROLE_GROUP_META[3];
              const isSelectedUser = Number(form.id) === Number(u.id);
              const isN8nReserved = isReservedForN8nUser(u);
              const isAdminRow = isAdminRoleLabel(roleLabel);
              const isDeleting = Number(deletingUserId) === Number(u.id);
              const identityTitle = [u.user, u.correo, u.telefono].filter(Boolean).join(' · ');
              const medicoChip = u.medico_nombre
                ? shortenDirectoryLabel(formatDoctorDisplayName(u.medico_nombre), 38)
                : '';
              const adminChip = u.administrativo_nombre
                ? shortenDirectoryLabel(u.administrativo_nombre, 34)
                : '';
              const callcenterChip = u.callcenter_nombre
                ? shortenDirectoryLabel(u.callcenter_nombre, 34)
                : '';
              return (
                <Fragment key={u.id}>
                  {showGroupHeader ? (
                    <div
                      className={`crm-au-dir-group crm-au-dir-group--${groupMeta.key}`}
                      role="presentation"
                    >
                      <span className="crm-au-dir-group__icon" aria-hidden="true">{groupMeta.glyph}</span>
                      <span className="crm-au-dir-group__label">{groupMeta.label}</span>
                      <span className="crm-au-dir-group__count">
                        {directoryGroupCounts[groupRank] || 0}
                      </span>
                      <span className="crm-au-dir-group__hint">{groupMeta.hint}</span>
                    </div>
                  ) : null}
                  <div
                    className={`crm-admin-user-row crm-admin-user-row--directory${isSelectedUser ? ' is-selected' : ''
                      }${Number(u.is_active) === 1 ? '' : ' is-inactive'}${isN8nReserved ? ' is-n8n-reserved' : ''
                      }${isDeleting ? ' is-deleting' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => startEdit(u)}
                    onKeyDown={(evt) => {
                      if (evt.key === 'Enter' || evt.key === ' ') {
                        evt.preventDefault();
                        startEdit(u);
                      }
                    }}
                  >
                    {isSelectedUser ? <span className="crm-admin-user-row__accent" aria-hidden="true" /> : null}
                    <div className="crm-admin-user-row__avatar-col">
                      {u.profile_photo_original || u.profile_photo ? (
                        <button
                          type="button"
                          className="crm-admin-user-row__avatar-btn"
                          onClick={(evt) => {
                            evt.stopPropagation();
                            setPreviewPhoto(u.profile_photo_original || u.profile_photo);
                            setPreviewLabel(u.user || 'usuario');
                          }}
                          title={`Ver foto de ${u.user || 'usuario'}`}
                        >
                          <img
                            src={u.profile_photo_original || u.profile_photo}
                            alt=""
                          />
                        </button>
                      ) : (
                        <div className="crm-admin-user-row__avatar" aria-hidden="true">
                          {String(u.user || 'U').charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="crm-admin-user-row__body">
                      <div className="crm-admin-user-row__line" title={identityTitle}>
                        <div className="crm-au-dir-cell crm-au-dir-cell--user">
                          <strong className="crm-admin-user-row__username">{u.user}</strong>
                          {Number(u.is_active) !== 1 ? (
                            <span className="crm-admin-user-row__status crm-admin-user-row__status--off">
                              Inactivo
                            </span>
                          ) : null}
                          {isN8nReserved ? (
                            <span className="crm-au-dir-chip crm-au-dir-chip--n8n" title="Reservado para n8n">
                              n8n
                            </span>
                          ) : null}
                        </div>
                        <div className="crm-au-dir-cell crm-au-dir-cell--role">
                          {roleLabel ? (
                            <span className="crm-au-dir-chip crm-au-dir-chip--role" title={`Rol: ${roleLabel}`}>
                              {roleLabel}
                            </span>
                          ) : null}
                        </div>
                        <div className="crm-au-dir-cell crm-au-dir-cell--identity">
                          {medicoChip ? (
                            <span
                              className="crm-au-dir-chip crm-au-dir-chip--admin"
                              title={`Médico: ${formatDoctorDisplayName(u.medico_nombre)}`}
                            >
                              <span className="crm-au-dir-chip__icon" aria-hidden="true">👤</span>
                              {medicoChip?.replace(
                                /^Dr\/a\.\s*(.*)$/i,
                                (_, nombre) =>
                                  `Dr/a. ${nombre
                                    .toLowerCase()
                                    .replace(/\b\w/g, l => l.toUpperCase())}`
                              )}
                            </span>
                          ) : null}
                          {adminChip ? (
                            <span
                              className="crm-au-dir-chip crm-au-dir-chip--admin"
                              title={`Administrativo: ${u.administrativo_nombre}`}
                            >
                              <span className="crm-au-dir-chip__icon" aria-hidden="true">👤</span>
                              {adminChip}
                            </span>
                          ) : null}
                          {callcenterChip ? (
                            <span
                              className="crm-au-dir-chip crm-au-dir-chip--admin"
                              title={`Call Center: ${u.callcenter_nombre}`}
                            >
                              <span className="crm-au-dir-chip__icon" aria-hidden="true">🎧</span>
                              {callcenterChip}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="crm-admin-user-row__toolbar">
                      <button
                        type="button"
                        className={`crm-admin-user-row__action-btn${isN8nReserved ? ' crm-admin-user-row__action-btn--view' : ''
                          }`}
                        onClick={(evt) => {
                          evt.stopPropagation();
                          startEdit(u);
                        }}
                        disabled={isDeleting}
                        title={
                          isN8nReserved
                            ? `Ver ${u.user} (solo lectura n8n)`
                            : `Editar ${u.user}`
                        }
                        aria-label={
                          isN8nReserved
                            ? `Ver ${u.user}, reservado para n8n`
                            : `Editar ${u.user}`
                        }
                      >
                        {isN8nReserved ? <UiIcon name="eye" size={14} /> : <UiIcon name="edit" size={14} />}
                      </button>
                      {!isN8nReserved && !isAdminRow ? (
                        <button
                          type="button"
                          className="crm-admin-user-row__action-btn crm-admin-user-row__action-btn--danger"
                          onClick={(evt) => requestDeleteUser(u, evt)}
                          disabled={isDeleting}
                          title={`Eliminar ${u.user}`}
                          aria-label={`Eliminar ${u.user}`}
                        >
                          <UiIcon name="trash" size={14} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
          {!loading && filteredUsers.length > 0 && directoryPageSize !== 'all' && directoryTotalPages > 1 ? (
            <nav className="crm-au-directory-pagination" aria-label="Paginación del directorio">
              <button
                type="button"
                className="crm-au-directory-pagination__btn"
                disabled={directoryPageSafe <= 1}
                onClick={() => setDirectoryPage(1)}
                aria-label="Primera página"
              >
                ⏮
              </button>
              <button
                type="button"
                className="crm-au-directory-pagination__btn"
                disabled={directoryPageSafe <= 1}
                onClick={() => setDirectoryPage((p) => Math.max(1, p - 1))}
                aria-label="Página anterior"
              >
                ◀
              </button>
              <span className="crm-au-directory-pagination__label">
                Pág {directoryPageSafe} de {directoryTotalPages}
              </span>
              <button
                type="button"
                className="crm-au-directory-pagination__btn"
                disabled={directoryPageSafe >= directoryTotalPages}
                onClick={() => setDirectoryPage((p) => Math.min(directoryTotalPages, p + 1))}
                aria-label="Página siguiente"
              >
                ▶
              </button>
              <button
                type="button"
                className="crm-au-directory-pagination__btn"
                disabled={directoryPageSafe >= directoryTotalPages}
                onClick={() => setDirectoryPage(directoryTotalPages)}
                aria-label="Última página"
              >
                ⏭
              </button>
            </nav>
          ) : null}
        </section>

        {form.id ? (
          isReservedForN8n ? (
            <N8nReservedUserPanel
              user={normalizedUser}
              correo={normalizedCorreo}
              telefono={normalizedTelefono}
              isActive={Number(form.is_active) === 1}
              roleLabel={editingRoleLabel}
              medicoNombre={form.medico_nombre}
              chatwootSummary={editingChatwootAgentSummary}
              permissionItems={editingPermissionItems}
              onBack={clearUserSelection}
            />
          ) : (
            <form onSubmit={handleEditFormSubmit} className="crm-admin-users-form crm-au-edit-form">
              <div className="crm-au-edit-form__sticky-head">
                <div className="crm-au-detail-head crm-au-detail-head--toolbar">
                  <AdminUserPanelBackButton onClick={clearUserSelection} />
                  <div className="crm-au-detail-head__content">
                    <div className="crm-au-detail-head__title">{normalizedUser || 'Usuario'}</div>
                    <div className="crm-au-detail-head__meta">
                      {editingRoleLabel ? `Rol · ${editingRoleLabel}` : 'Sin rol asignado'}
                      {normalizedCorreo ? ` · ${normalizedCorreo}` : ''}
                      {form.medico_nombre ? ` · ${formatDoctorDisplayName(form.medico_nombre)}` : ''}
                      {editingChatwootAgentSummary ? (
                        <>
                          {' · Agente Chatwoot · '}
                          <strong
                            className={
                              editingChatwootAgentSummary.linked
                                ? 'crm-au-detail-head__meta-accent'
                                : 'crm-au-detail-head__meta-muted'
                            }
                          >
                            {editingChatwootAgentSummary.metaLabel}
                          </strong>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <span
                    className={`crm-au-status-pill ${Number(form.is_active) === 1 ? 'crm-au-status-pill--active' : 'crm-au-status-pill--inactive'
                      }`}
                  >
                    {Number(form.is_active) === 1 ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {isReservedForN8n ? (
                  <div className="crm-au-alert crm-au-alert--warn">Usuario reservado para n8n (solo lectura).</div>
                ) : (
                  <nav className="crm-au-form-steps" aria-label="Pasos de edición">
                    <button
                      type="button"
                      className={`crm-au-modal-steps__item${editFormStep === 1 ? ' is-active' : ''}${editFormStep > 1 ? ' is-done' : ''
                        }`}
                      onClick={() => (editFormStep > 1 ? setEditFormStep(1) : undefined)}
                      aria-current={editFormStep === 1 ? 'step' : undefined}
                    >
                      <span className="crm-au-modal-steps__num">1</span>
                      <span>Datos y rol</span>
                    </button>
                    <span className="crm-au-modal-steps__line" aria-hidden="true" />
                    <button
                      type="button"
                      className={`crm-au-modal-steps__item${editFormStep === 2 ? ' is-active' : ''}${editFormStep > 2 ? ' is-done' : ''
                        }`}
                      disabled={!canAdvanceEditStep1 && editFormStep < 2}
                      onClick={() => {
                        if (editFormStep === 2) return;
                        goEditFormStep2();
                      }}
                      aria-current={editFormStep === 2 ? 'step' : undefined}
                    >
                      <span className="crm-au-modal-steps__num">2</span>
                      <span>{userFormStep2Label}</span>
                    </button>
                    {userFormIsAdministrativoThreeStep ? (
                      <>
                        <span className="crm-au-modal-steps__line" aria-hidden="true" />
                        <button
                          type="button"
                          className={`crm-au-modal-steps__item${editFormStep === 3 ? ' is-active' : ''}`}
                          disabled={editFormStep < 3 && editFormStep < 2}
                          onClick={() => {
                            if (editFormStep === 3) return;
                            if (editFormStep < 2) goEditFormStep2();
                            else goEditFormStep3();
                          }}
                          aria-current={editFormStep === 3 ? 'step' : undefined}
                        >
                          <span className="crm-au-modal-steps__num">3</span>
                          <span>{userFormStep3Label}</span>
                        </button>
                      </>
                    ) : null}
                  </nav>
                )}

                {!isReservedForN8n && editFormStep === 1 ? (
                  <div className="crm-au-alert crm-au-alert--info">
                    Paso 1 · Datos, rol y permisos. La contraseña es opcional si no querés cambiarla.
                  </div>
                ) : null}
                {!isReservedForN8n && editFormStep === 2 && userFormStep2IsMedico ? (
                  <div className="crm-au-alert crm-au-alert--info">
                    Paso 2 · Elegí el médico visible del catálogo. Los pedidos derivados a ese médico llegarán a la
                    bandeja de <strong>Pedidos/Recetas</strong> de este usuario.
                  </div>
                ) : null}
                {!isReservedForN8n && editFormStep === 2 && !userFormStep2IsMedico ? (
                  <div className="crm-au-alert crm-au-alert--info">
                    Paso 2 · Vinculá o gestioná el operador de Chatwoot para WhatsApp. Los cambios de vínculo se guardan al
                    instante{userFormIsAdministrativoThreeStep ? '; en el paso 3 elegís la persona del catálogo administrativo' : ''}.
                  </div>
                ) : null}
                {!isReservedForN8n && editFormStep === 3 && userFormIsAdministrativoThreeStep ? (
                  <div className="crm-au-alert crm-au-alert--info">
                    {selectedRoleIsCallcenter
                      ? 'Paso 3 · Elegí la persona de Call Center visible del catálogo para vincular este usuario.'
                      : <>Paso 3 · Elegí la persona administrativa visible del catálogo. Al emitir tratamientos con emisor Administrativo quedará asignada a <strong>Recetas a emitir</strong>.</>}
                  </div>
                ) : null}
              </div>

              <div className="crm-au-edit-form__body">
                {isReservedForN8n || editFormStep === 1 ? (
                  <>
                    <div className="crm-au-section">
                      <div className="crm-au-section__title">Datos del usuario</div>
                      <label
                        className={`crm-au-field crm-admin-users-field-row${createUserNameTaken ? ' crm-au-field--invalid' : ''
                          }`}
                      >
                        <span className="crm-au-field__label">Usuario *</span>
                        <div className="crm-au-field__input-stack">
                          <input
                            className="crm-profile-input"
                            style={fieldInputStyle}
                            placeholder="Ej: admin3"
                            value={form.user}
                            onChange={(e) => setForm((p) => ({ ...p, user: e.target.value }))}
                            disabled={isReservedForN8n}
                            aria-invalid={createUserNameTaken || undefined}
                          />
                          {!isReservedForN8n ? (
                            <span
                              className={`crm-au-field__hint-slot${createUserNameTaken ? ' crm-au-field__hint-slot--error' : ''
                                }`}
                              role={createUserNameTaken ? 'alert' : undefined}
                              aria-live="polite"
                            >
                              {createUserNameTaken ? (
                                <>
                                  Ya existe el usuario{' '}
                                  <strong>{createUserDuplicate.userMatch?.user || normalizedUser}</strong>
                                </>
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                      </label>
                      <label
                        className={`crm-au-field crm-admin-users-field-row${createUserCorreoTaken ? ' crm-au-field--invalid' : ''
                          }`}
                      >
                        <span className="crm-au-field__label">Correo *</span>
                        <div className="crm-au-field__input-stack">
                          <input
                            className="crm-profile-input"
                            style={fieldInputStyle}
                            type="email"
                            placeholder="usuario@empresa.com"
                            value={form.correo}
                            onChange={(e) => setForm((p) => ({ ...p, correo: e.target.value }))}
                            disabled={isReservedForN8n}
                            aria-invalid={createUserCorreoTaken || undefined}
                          />
                          {!isReservedForN8n ? (
                            <span
                              className={`crm-au-field__hint-slot${createUserCorreoTaken ? ' crm-au-field__hint-slot--error' : ''
                                }`}
                              role={createUserCorreoTaken ? 'alert' : undefined}
                              aria-live="polite"
                            >
                              {createUserCorreoTaken ? (
                                <>
                                  Ya está registrado el correo{' '}
                                  <strong>{createUserDuplicate.correoMatch?.correo || normalizedCorreo}</strong>
                                </>
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                      </label>
                      <label className="crm-au-field crm-admin-users-field-row">
                        <span className="crm-au-field__label">Teléfono</span>
                        <input
                          className="crm-profile-input"
                          style={fieldInputStyle}
                          placeholder="Ej: 2612413237"
                          value={form.telefono}
                          onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))}
                          disabled={isReservedForN8n}
                        />
                      </label>
                      <CrmPasswordField
                        id="edit-user-password"
                        label="Nueva contraseña"
                        placeholder="Opcional — dejar vacío para mantener"
                        value={form.password}
                        onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                        disabled={isReservedForN8n}
                      />
                      <label className="crm-au-field crm-admin-users-field-row">
                        <span className="crm-au-field__label">Activo</span>
                        <select
                          value={form.is_active}
                          onChange={(e) => setForm((p) => ({ ...p, is_active: Number(e.target.value) }))}
                          className="crm-profile-input"
                          style={fieldInputStyle}
                          disabled={isReservedForN8n}
                        >
                          <option value={1}>Sí</option>
                          <option value={0}>No</option>
                        </select>
                      </label>
                    </div>

                    <RolesPermissionsAccordion
                      expandedPanel={expandedRpPanel}
                      onTogglePanel={toggleRpPanel}
                      roleOptions={roleOptions}
                      form={form}
                      permissions={permissions}
                      syncingRolePerms={syncingRolePerms}
                      onSelectRole={(roleId) => selectRoleAndSyncPermissions(roleId)}
                      disabled={isReservedForN8n}
                      radioName="edit-role-single"
                    />

                    {isReservedForN8n ? (
                      <div className="crm-au-chatwoot-slot">
                        <ChatwootAgentField
                          value={form.chatwoot_agent_id}
                          agentName={form.chatwoot_agent_name}
                          savingLink={savingChatwootLink}
                          onAgentIdChange={handleChatwootAgentIdChange}
                          onAgentNameChange={(next) => setForm((p) => ({ ...p, chatwoot_agent_name: next }))}
                          onSaveAgentName={saveChatwootAgentName}
                          agents={chatwootAgents}
                          pickerAgents={selectableChatwootAgents}
                          loading={chatwootAgentsLoading}
                          loadError={chatwootAgentsError}
                          disabled
                          displayName={normalizedUser}
                          emailLooksValid={emailLooksValid}
                          onCreateInChatwoot={createChatwootAgentForForm}
                          creatingChatwoot={creatingChatwootAgent}
                          onDeleteFromChatwoot={deleteChatwootAgentForForm}
                          deletingChatwoot={deletingChatwootAgent}
                          savingAgentName={savingChatwootAgentName}
                          fieldInputStyle={fieldInputStyle}
                          createEmailBlocked={isBlockedChatwootAgentEmail(normalizedCorreo)}
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}

                {!isReservedForN8n && editFormStep === 2 ? (
                  userFormStep2IsMedico ? (
                    <div className="crm-au-medico-step">
                      <MedicoCatalogField
                        value={form.medico_id}
                        medicos={visibleMedicos}
                        loading={visibleMedicosLoading}
                        loadError={visibleMedicosError}
                        takenByOther={medicoLinksTakenByOther}
                        disabled={false}
                        fieldInputStyle={fieldInputStyle}
                        onChange={(nextId) => {
                          const picked = visibleMedicos.find((m) => String(m.id) === String(nextId));
                          setForm((p) => ({
                            ...p,
                            medico_id: nextId ? String(nextId) : '',
                            medico_nombre: picked?.nombre || ''
                          }));
                        }}
                      />
                    </div>
                  ) : (
                    <div className="crm-au-chatwoot-slot">
                      <ChatwootAgentField
                        value={form.chatwoot_agent_id}
                        agentName={form.chatwoot_agent_name}
                        savingLink={savingChatwootLink}
                        onAgentIdChange={handleChatwootAgentIdChange}
                        onAgentNameChange={(next) => setForm((p) => ({ ...p, chatwoot_agent_name: next }))}
                        onSaveAgentName={saveChatwootAgentName}
                        agents={chatwootAgents}
                        pickerAgents={selectableChatwootAgents}
                        loading={chatwootAgentsLoading}
                        loadError={chatwootAgentsError}
                        disabled={false}
                        displayName={normalizedUser}
                        emailLooksValid={emailLooksValid}
                        onCreateInChatwoot={createChatwootAgentForForm}
                        creatingChatwoot={creatingChatwootAgent}
                        onDeleteFromChatwoot={deleteChatwootAgentForForm}
                        deletingChatwoot={deletingChatwootAgent}
                        savingAgentName={savingChatwootAgentName}
                        fieldInputStyle={fieldInputStyle}
                        createEmailBlocked={isBlockedChatwootAgentEmail(normalizedCorreo)}
                      />
                    </div>
                  )
                ) : null}

                {!isReservedForN8n && editFormStep === 3 && userFormIsAdministrativoThreeStep ? (
                  <div className="crm-au-medico-step">
                    {selectedRoleIsCallcenter ? (
                      <CallcenterCatalogField
                        value={form.callcenter_id}
                        callcenters={visibleCallcenters}
                        loading={visibleCallcentersLoading}
                        loadError={visibleCallcentersError}
                        takenByOther={callcenterLinksTakenByOther}
                        disabled={false}
                        fieldInputStyle={fieldInputStyle}
                        onChange={(nextId) => {
                          const picked = visibleCallcenters.find((a) => String(a.id) === String(nextId));
                          setForm((p) => ({
                            ...p,
                            callcenter_id: nextId ? String(nextId) : '',
                            callcenter_nombre: picked?.nombre || ''
                          }));
                        }}
                      />
                    ) : (
                      <AdministrativoCatalogField
                        value={form.administrativo_id}
                        administrativos={visibleAdministrativos}
                        loading={visibleAdministrativosLoading}
                        loadError={visibleAdministrativosError}
                        takenByOther={administrativoLinksTakenByOther}
                        disabled={false}
                        fieldInputStyle={fieldInputStyle}
                        onChange={(nextId) => {
                          const picked = visibleAdministrativos.find((a) => String(a.id) === String(nextId));
                          setForm((p) => ({
                            ...p,
                            administrativo_id: nextId ? String(nextId) : '',
                            administrativo_nombre: picked?.nombre || ''
                          }));
                        }}
                      />
                    )}
                  </div>
                ) : null}
              </div>

              {isReservedForN8n ? (
                <button
                  className="crm-profile-primary-btn crm-au-save-btn"
                  disabled={!canSubmit || isReservedForN8n}
                  type="submit"
                >
                  {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              ) : (
                <footer
                  className={`crm-au-edit-footer${editFormStep >= 2 ? ' crm-au-edit-footer--split' : ''
                    }`}
                >
                  {editFormStep === 2 ? (
                    <button
                      type="button"
                      className="crm-au-modal__btn crm-au-modal__btn--ghost"
                      onClick={() => setEditFormStep(1)}
                    >
                      Atrás
                    </button>
                  ) : null}
                  {editFormStep === 3 ? (
                    <button
                      type="button"
                      className="crm-au-modal__btn crm-au-modal__btn--ghost"
                      onClick={() => setEditFormStep(2)}
                    >
                      Atrás
                    </button>
                  ) : null}
                  <button
                    className="crm-profile-primary-btn crm-au-save-btn crm-au-edit-footer__primary"
                    disabled={
                      editFormStep === 1
                        ? !canAdvanceEditStep1 || !canSubmit || saving
                        : editFormStep === 2 && userFormIsAdministrativoThreeStep
                          ? !canSubmit || saving
                          : editFormStep === 2 && userFormStep2IsMedico
                            ? !canAdvanceUserMedicoStep2 || !canSubmit || saving
                            : editFormStep === 3
                              ? (selectedRoleIsCallcenter ? !String(form.callcenter_id || '').trim() : !canAdvanceUserAdministrativoStep2) || !canSubmit || saving
                              : !canSubmit || saving
                    }
                    type="submit"
                  >
                    {saving
                      ? 'Guardando…'
                      : editFormStep === 1
                        ? 'Siguiente'
                        : editFormStep === 2 && userFormIsAdministrativoThreeStep
                          ? 'Siguiente'
                          : 'Guardar cambios'}
                  </button>
                </footer>
              )}
            </form>
          )
        ) : (
          <section className="crm-admin-users-form crm-admin-users-form--empty" aria-label="Panel de edición vacío">
            <div className="crm-au-panel-empty">
              <div className="crm-au-panel-empty__icon" aria-hidden="true">
                <span className="crm-au-panel-empty__icon-ring" />
                <span className="crm-au-panel-empty__icon-glyph">
                  <UiIcon name="users" size={22} />
                </span>
              </div>
              <h3 className="crm-au-panel-empty__title">Sin usuario seleccionado</h3>
              <p className="crm-au-panel-empty__copy">
                Elegí un usuario del directorio para editar datos, rol, permisos y WhatsApp. La cuenta n8n se
                puede ver en solo lectura.
              </p>
              <div className="crm-au-panel-empty__chips" aria-hidden="true">
                <span className="crm-au-panel-empty__chip">Datos y rol</span>
                <span className="crm-au-panel-empty__chip">WhatsApp / Médico</span>
                <span className="crm-au-panel-empty__chip">Permisos</span>
              </div>
            </div>
          </section>
        )}
      </div>

      {isCreateModalOpen && typeof document !== 'undefined'
        ? createPortal(
          <dialog
            ref={createModalDialogRef}
            className="crm-admin-create-modal crm-au-modal crm-au-create-dialog"
            aria-labelledby="crm-au-create-modal-title"
            onCancel={(e) => {
              e.preventDefault();
              closeCreateModal();
            }}
            onClick={(e) => {
              const dlg = createModalDialogRef.current;
              if (!dlg) return;
              const rect = dlg.getBoundingClientRect();
              const inside =
                e.clientX >= rect.left &&
                e.clientX <= rect.right &&
                e.clientY >= rect.top &&
                e.clientY <= rect.bottom;
              if (!inside) closeCreateModal();
            }}
          >

            <header className="crm-au-modal__hero">
              <div className="crm-au-modal__hero-main">
                <span className="crm-au-modal__hero-icon" aria-hidden="true">
                  <UiIcon name="users" />
                </span>
                <div>
                  <h3 id="crm-au-create-modal-title" className="crm-admin-create-modal__title">
                    Nuevo usuario
                  </h3>
                  <p className="crm-au-modal__subtitle">
                    {createModalStep === 1
                      ? 'Paso 1 · Datos del usuario, rol y contraseña'
                      : createModalStep === 2 && userFormStep2IsMedico
                        ? 'Paso 2 · Médico visible del catálogo'
                        : createModalStep === 2
                          ? 'Paso 2 · WhatsApp / Chatwoot (opcional)'
                          : selectedRoleIsCallcenter
                            ? 'Paso 3 · Persona de Call Center del catálogo'
                            : 'Paso 3 · Persona administrativa del catálogo'}
                  </p>
                </div>
              </div>
              {/* <button
              type="button"
              className="crm-au-modal__close"
              onClick={closeCreateModal}
              aria-label="Cerrar"
            >
              ✕
            </button> */}
            </header>

            <nav className="crm-au-modal-steps" aria-label="Pasos del alta de usuario">
              <button
                type="button"
                className={`crm-au-modal-steps__item${createModalStep === 1 ? ' is-active' : ''}${createModalStep > 1 ? ' is-done' : ''
                  }`}
                onClick={() => (createModalStep > 1 ? setCreateModalStep(1) : undefined)}
                aria-current={createModalStep === 1 ? 'step' : undefined}
              >
                <span className="crm-au-modal-steps__num">1</span>
                <span>Datos y rol</span>
              </button>
              <span className="crm-au-modal-steps__line" aria-hidden="true" />
              <button
                type="button"
                className={`crm-au-modal-steps__item${createModalStep === 2 ? ' is-active' : ''}${createModalStep > 2 ? ' is-done' : ''
                  }`}
                disabled={!canAdvanceCreateStep1 && createModalStep < 2}
                onClick={() => {
                  if (createModalStep === 2) return;
                  goCreateModalStep2();
                }}
                aria-current={createModalStep === 2 ? 'step' : undefined}
              >
                <span className="crm-au-modal-steps__num">2</span>
                <span>{userFormStep2Label}</span>
              </button>
              {userFormIsAdministrativoThreeStep ? (
                <>
                  <span className="crm-au-modal-steps__line" aria-hidden="true" />
                  <button
                    type="button"
                    className={`crm-au-modal-steps__item${createModalStep === 3 ? ' is-active' : ''}`}
                    disabled={createModalStep < 3 && createModalStep < 2}
                    onClick={() => {
                      if (createModalStep === 3) return;
                      if (createModalStep < 2) goCreateModalStep2();
                      else goCreateModalStep3();
                    }}
                    aria-current={createModalStep === 3 ? 'step' : undefined}
                  >
                    <span className="crm-au-modal-steps__num">3</span>
                    <span>{userFormStep3Label}</span>
                  </button>
                </>
              ) : null}
            </nav>

            <form
              onSubmit={handleCreateModalFormSubmit}
              className="crm-au-modal__form"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="crm-admin-create-modal__body">
                {createModalStep === 1 ? (
                  <>
                    <section className="crm-au-section">
                      <div className="crm-au-section__title">Datos del usuario</div>
                      <div className="crm-au-field-grid">
                        <label
                          className={`crm-au-field crm-au-field--stacked${createUserNameTaken ? ' crm-au-field--invalid' : ''
                            }`}
                        >
                          <span className="crm-au-field__label">Usuario *</span>
                          <input
                            className="crm-profile-input"
                            placeholder="Ej: operador_1"
                            value={form.user}
                            onChange={(e) => setForm((p) => ({ ...p, user: e.target.value }))}
                            autoComplete="off"
                            aria-invalid={createUserNameTaken || undefined}
                          />
                          <span
                            className={`crm-au-field__hint-slot${createUserNameTaken ? ' crm-au-field__hint-slot--error' : ''
                              }`}
                            role={createUserNameTaken ? 'alert' : undefined}
                            aria-live="polite"
                          >
                            {createUserNameTaken ? (
                              <>
                                Ya existe el usuario{' '}
                                <strong>{createUserDuplicate.userMatch?.user || normalizedUser}</strong>
                              </>
                            ) : null}
                          </span>
                        </label>
                        <label
                          className={`crm-au-field crm-au-field--stacked${createUserCorreoTaken ? ' crm-au-field--invalid' : ''
                            }`}
                        >
                          <span className="crm-au-field__label">Correo *</span>
                          <input
                            className="crm-profile-input"
                            type="email"
                            placeholder="usuario@empresa.com"
                            value={form.correo}
                            onChange={(e) => setForm((p) => ({ ...p, correo: e.target.value }))}
                            autoComplete="off"
                            aria-invalid={createUserCorreoTaken || undefined}
                          />
                          <span
                            className={`crm-au-field__hint-slot${createUserCorreoTaken ? ' crm-au-field__hint-slot--error' : ''
                              }`}
                            role={createUserCorreoTaken ? 'alert' : undefined}
                            aria-live="polite"
                          >
                            {createUserCorreoTaken ? (
                              <>
                                Ya está registrado el correo{' '}
                                <strong>{createUserDuplicate.correoMatch?.correo || normalizedCorreo}</strong>
                              </>
                            ) : null}
                          </span>
                        </label>
                        <label className="crm-au-field crm-au-field--stacked">
                          <span className="crm-au-field__label">Teléfono</span>
                          <input
                            className="crm-profile-input"
                            placeholder="Ej: 261123456"
                            value={form.telefono}
                            onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))}
                          />
                        </label>
                        <CrmPasswordField
                          id="create-user-password"
                          label="Contraseña inicial"
                          layout="stacked"
                          required
                          placeholder="Mínimo 6 caracteres"
                          value={form.password}
                          onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                        />
                        <div className="crm-au-field crm-au-field--stacked crm-au-field--switch-row">
                          <span className="crm-au-field__label">Usuario activo</span>
                          <div className="crm-au-active-switch">
                            <CrmToggleSwitch
                              checked={Number(form.is_active) === 1}
                              ariaLabel="Usuario activo"
                              onChange={(on) => setForm((p) => ({ ...p, is_active: on ? 1 : 0 }))}
                            />
                            <span className="crm-au-active-switch__label">
                              {Number(form.is_active) === 1 ? 'Activo' : 'Inactivo'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </section>

                    <RolesPermissionsAccordion
                      expandedPanel={expandedRpPanel}
                      onTogglePanel={toggleRpPanel}
                      roleOptions={roleOptions}
                      form={form}
                      permissions={permissions}
                      syncingRolePerms={syncingRolePerms}
                      onSelectRole={(roleId) => selectRoleAndSyncPermissions(roleId, { autoShowPermissions: true })}
                      disabled={false}
                      radioName="create-role-single"
                      rolePickerVariant="switch"
                    />

                    {selectedRoleIsMedico ? (
                      <div className="crm-au-alert crm-au-alert--info">
                        Paso 2 · <strong>Médico</strong> del catálogo visible. No se configura WhatsApp para este rol.
                      </div>
                    ) : null}
                    {selectedRoleIsAdministrativo ? (
                      <div className="crm-au-alert crm-au-alert--info">
                        Este rol usa <strong>3 pasos</strong>: datos, agente Chatwoot (paso 2) y persona del catálogo
                        administrativo (paso 3).
                      </div>
                    ) : null}
                    {selectedRoleIsCallcenter ? (
                      <div className="crm-au-alert crm-au-alert--info">
                        Este rol usa <strong>3 pasos</strong>: datos, agente Chatwoot (paso 2) y persona del catálogo
                        de Call Center (paso 3).
                      </div>
                    ) : null}
                  </>
                ) : createModalStep === 2 && userFormStep2IsMedico ? (
                  <div className="crm-au-medico-step">
                    <div className="crm-au-alert crm-au-alert--info">
                      Paso 2 · Elegí el médico visible del catálogo. Las derivaciones a ese médico llegarán a la bandeja de
                      este usuario en <strong>Pedidos/Recetas</strong>.
                    </div>
                    <MedicoCatalogField
                      value={form.medico_id}
                      medicos={visibleMedicos}
                      loading={visibleMedicosLoading}
                      loadError={visibleMedicosError}
                      takenByOther={medicoLinksTakenByOther}
                      disabled={false}
                      fieldInputStyle={fieldInputStyle}
                      onChange={(nextId) => {
                        const picked = visibleMedicos.find((m) => String(m.id) === String(nextId));
                        setForm((p) => ({
                          ...p,
                          medico_id: nextId ? String(nextId) : '',
                          medico_nombre: picked?.nombre || ''
                        }));
                      }}
                    />
                  </div>
                ) : createModalStep === 2 ? (
                  <div className="crm-au-chatwoot-slot">
                    <div className="crm-au-alert crm-au-alert--info">
                      Paso 2 · Vinculá un operador de Chatwoot para WhatsApp o creá uno nuevo.
                      {userFormIsAdministrativoThreeStep
                        ? ' En el paso 3 elegís la persona del catálogo administrativo.'
                        : ' Podés omitir este paso y configurarlo después editando el usuario.'}
                    </div>
                    <ChatwootAgentField
                      value={form.chatwoot_agent_id}
                      agentName={form.chatwoot_agent_name}
                      savingLink={savingChatwootLink}
                      onAgentIdChange={handleChatwootAgentIdChange}
                      onAgentNameChange={(next) => setForm((p) => ({ ...p, chatwoot_agent_name: next }))}
                      onSaveAgentName={saveChatwootAgentName}
                      agents={chatwootAgents}
                      pickerAgents={selectableChatwootAgents}
                      loading={chatwootAgentsLoading}
                      loadError={chatwootAgentsError}
                      disabled={false}
                      displayName={normalizedUser}
                      emailLooksValid={emailLooksValid}
                      onCreateInChatwoot={createChatwootAgentForForm}
                      creatingChatwoot={creatingChatwootAgent}
                      onDeleteFromChatwoot={deleteChatwootAgentForForm}
                      deletingChatwoot={deletingChatwootAgent}
                      savingAgentName={savingChatwootAgentName}
                      fieldInputStyle={fieldInputStyle}
                      createEmailBlocked={isBlockedChatwootAgentEmail(normalizedCorreo)}
                    />
                  </div>
                ) : (
                  <div className="crm-au-medico-step">
                    {selectedRoleIsCallcenter ? (
                      <>
                        <div className="crm-au-alert crm-au-alert--info">
                          Paso 3 · Elegí la persona de Call Center visible del catálogo para vincular este usuario.
                        </div>
                        <CallcenterCatalogField
                          value={form.callcenter_id}
                          callcenters={visibleCallcenters}
                          loading={visibleCallcentersLoading}
                          loadError={visibleCallcentersError}
                          takenByOther={callcenterLinksTakenByOther}
                          disabled={false}
                          fieldInputStyle={fieldInputStyle}
                          onChange={(nextId) => {
                            const picked = visibleCallcenters.find((a) => String(a.id) === String(nextId));
                            setForm((p) => ({
                              ...p,
                              callcenter_id: nextId ? String(nextId) : '',
                              callcenter_nombre: picked?.nombre || ''
                            }));
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <div className="crm-au-alert crm-au-alert--info">
                          Paso 3 · Elegí la persona administrativa visible. Los tratamientos con emisor Administrativo quedarán
                          asignados a <strong>Recetas a emitir</strong> de esta persona.
                        </div>
                        <AdministrativoCatalogField
                          value={form.administrativo_id}
                          administrativos={visibleAdministrativos}
                          loading={visibleAdministrativosLoading}
                          loadError={visibleAdministrativosError}
                          takenByOther={administrativoLinksTakenByOther}
                          disabled={false}
                          fieldInputStyle={fieldInputStyle}
                          onChange={(nextId) => {
                            const picked = visibleAdministrativos.find((a) => String(a.id) === String(nextId));
                            setForm((p) => ({
                              ...p,
                              administrativo_id: nextId ? String(nextId) : '',
                              administrativo_nombre: picked?.nombre || ''
                            }));
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>

              <footer
                className={`crm-au-modal__footer${createModalStep >= 2 ? ' crm-au-modal__footer--three' : ''
                  }`}
              >
                <button
                  type="button"
                  className="crm-au-modal__btn crm-au-modal__btn--ghost"
                  onClick={closeCreateModal}
                >
                  Cancelar
                </button>
                {createModalStep === 2 ? (
                  <button
                    type="button"
                    className="crm-au-modal__btn crm-au-modal__btn--ghost"
                    onClick={() => setCreateModalStep(1)}
                  >
                    Atrás
                  </button>
                ) : null}
                {createModalStep === 3 ? (
                  <button
                    type="button"
                    className="crm-au-modal__btn crm-au-modal__btn--ghost"
                    onClick={() => setCreateModalStep(2)}
                  >
                    Atrás
                  </button>
                ) : null}
                <button
                  className="crm-au-modal__btn crm-au-modal__btn--primary"
                  disabled={
                    createModalStep === 1
                      ? !canAdvanceCreateStep1 || saving
                      : createModalStep === 2 && userFormIsAdministrativoThreeStep
                        ? !canSubmit || saving
                        : createModalStep === 2 && userFormStep2IsMedico
                          ? !canAdvanceUserMedicoStep2 || !canSubmit || saving
                          : createModalStep === 3
                            ? (selectedRoleIsCallcenter ? !String(form.callcenter_id || '').trim() : !canAdvanceUserAdministrativoStep2) || !canSubmit || saving
                            : !canSubmit || saving
                  }
                  type="submit"
                >
                  {saving
                    ? 'Guardando…'
                    : createModalStep === 1
                      ? 'Siguiente'
                      : createModalStep === 2 && userFormIsAdministrativoThreeStep
                        ? 'Siguiente'
                        : 'Crear usuario'}
                </button>
              </footer>
            </form>
          </dialog>,
          document.body
        )
        : null}

      {previewPhoto ? (
        <div className="crm-au-photo-backdrop" onClick={() => setPreviewPhoto('')} role="dialog" aria-modal="true">
          <div className="crm-au-photo-frame" onClick={(evt) => evt.stopPropagation()}>
            <img src={previewPhoto} alt={`Foto de perfil de ${previewLabel}`} />
            <button
              type="button"
              className="crm-au-photo-close"
              onClick={() => setPreviewPhoto('')}
              aria-label="Cerrar vista de foto"
            >
              ✕
            </button>
          </div>
        </div>
      ) : null}

    </div>
  );
}

export default AdminUsuarios;
